import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { apiService, ApiError, type FieldError } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { AccountInformation, CreditInquiry, PersonalInformation, CreditFactor, VerificationQuestion, PublicRecord } from '../../types/api';
import { formatDate } from '../../lib/utils';
import { Loader2, RefreshCw, Copy, Check, ArrowUpDown, ArrowUp, ArrowDown, Download, FileText, CreditCard, CircleX, Clock, AlertTriangle, CheckCircle2, TrendingUp, TrendingDown, ShieldCheck } from 'lucide-react';
import { CreditReportView } from './CreditReportView';
import { CreditHistory } from './CreditHistory';
import { ReportDateSelector } from './ReportDateSelector';
import { UserNotes } from './UserNotes';
import { CreditRoadmap } from './CreditRoadmap';

type SortDirection = 'asc' | 'desc' | null;
type AccountSortField = 'creditorName' | 'accountType' | 'accountStatus' | 'currentBalance' | 'creditLimit' | 'bureau' | 'dateOpened';
type InquirySortField = 'company' | 'date' | 'bureau';
const CRS_BUREAU_API_UNAVAILABLE_ERROR = 'The Bureau API does not respond (VS 3 unavailable)';

export function CreditAnalytics({ isActive }: { isActive?: boolean }) {
  const { user, refreshUser } = useAuth();
  const { analytics, setAnalytics, isLoading, setIsLoading } = useAnalytics();
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [scoreDeltas, setScoreDeltas] = useState<any>(null);
  const [accountSort, setAccountSort] = useState<{ field: AccountSortField | null; direction: SortDirection }>({ field: null, direction: null });
  const [inquirySort, setInquirySort] = useState<{ field: InquirySortField | null; direction: SortDirection }>({ field: null, direction: null });
  const [inquiryBureauFilter, setInquiryBureauFilter] = useState<string>('all');
  const [accountStatusFilter, setAccountStatusFilter] = useState<string>('all');
  const [expandedAccountId, setExpandedAccountId] = useState<string | null>(null);
  const [showRefreshConfirm, setShowRefreshConfirm] = useState(false);
  const [refreshConfirmAction, setRefreshConfirmAction] = useState<'report' | 'verify'>('report');
  const [isRefreshingFromIIQ, setIsRefreshingFromIIQ] = useState(false);
  const [showPdfConfirm, setShowPdfConfirm] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [showForceRefreshConfirm, setShowForceRefreshConfirm] = useState(false);
  const [isForceRefreshing, setIsForceRefreshing] = useState(false);
  const [forceRefreshError, setForceRefreshError] = useState('');
  const [forceRefreshToast, setForceRefreshToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  
  // Report date selector state (for manager view only)
  const [selectedReportDate, setSelectedReportDate] = useState<string | null>(null);
  const [selectedAnalyticsId, setSelectedAnalyticsId] = useState<string | null>(null);
  const [selectedBureauCount, setSelectedBureauCount] = useState<number | null>(null);
  const [isLoadingReports, setIsLoadingReports] = useState(false);
  
  // Long polling state for placeholder records
  const [pollingIntervalId, setPollingIntervalId] = useState<NodeJS.Timeout | null>(null);
  
  // Refresh trigger for ReportDateSelector
  const [reportRefreshTrigger, setReportRefreshTrigger] = useState(0);
  
  // Verification flow state
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [verificationQuestions, setVerificationQuestions] = useState<VerificationQuestion[]>([]);
  const [selectedAnswers, setSelectedAnswers] = useState<string[]>([]);
  const [isLoadingVerification, setIsLoadingVerification] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const [verificationStep, setVerificationStep] = useState<'questions' | 'submitting' | 'success'>('questions');
  
  // CRS verification state
  const [showCRSVerificationDialog, setShowCRSVerificationDialog] = useState(false);
  const [isCRSVerifying, setIsCRSVerifying] = useState(false);
  const [crsVerificationResult, setCRSVerificationResult] = useState<'pending' | 'success' | 'failed'>('pending');
  const [crsVerificationMessage, setCRSVerificationMessage] = useState('');
  const [crsVerificationFieldErrors, setCrsVerificationFieldErrors] = useState<FieldError[]>([]);
  
  // 477 error state (microfrontend required)
  const [showMicrofrontendRequired, setShowMicrofrontendRequired] = useState(false);
  
  // Check if viewing as manager/admin
  const managerSession = localStorage.getItem('managerSession');
  const isManagerView = !!managerSession;

  const normalizeCRSErrorMessage = (message: string): string => (
    message.includes('VS 3 unavailable')
      ? CRS_BUREAU_API_UNAVAILABLE_ERROR
      : message
  );

  const fetchAnalytics = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      // If manager is viewing, only fetch when a report date is selected
      // (ReportDateSelector will trigger this with the initial report)
      if (isManagerView && user?.id) {
        if (selectedReportDate && selectedBureauCount) {
          const lang = apiService.getLanguage();
          const data = await apiService.getCreditAnalyticsByDate(
            user.id,
            selectedReportDate,
            selectedBureauCount,
            lang !== 'en' ? lang : undefined
          );
          setAnalytics(data);
        } else {
          // Don't fetch yet - wait for ReportDateSelector to initialize
          setIsLoading(false);
          return;
        }
      } else {
        // Regular user view - fetch the latest report
        // Use 3 bureaus if maxPlanEnabled OR revenueCatId is not null, otherwise 1 bureau
        const bureauCount = (user?.maxPlanEnabled || user?.revenueCatId) ? 3 : 1;
        const data = await apiService.getCreditAnalytics(bureauCount);
        setAnalytics(data);
      }
      
      // Fetch score deltas
      await fetchScoreDeltas();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to fetch credit analytics');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchScoreDeltas = async () => {
    try {
      if (!user?.id) return;
      const response = await apiService.getScoreDeltas(user.id);
      if (response.success && response.deltas) {
        setScoreDeltas(response);
      }
    } catch (err) {
      console.error('Failed to fetch score deltas:', err);
      // Don't set error - deltas are optional
    }
  };

  const handleGetLatestReport = async () => {
    if (user?.enrolled) {
      setRefreshConfirmAction('report');
    } else {
      setRefreshConfirmAction('verify');
    }
    setShowRefreshConfirm(true);
  };

  const handleRefreshCurrentReport = async () => {
    // Use the same logic as handleGetLatestReport to check enrollment first
    await handleGetLatestReport();
  };

  const handleForceRefresh = async () => {
    if (!user?.id) return;
    setIsForceRefreshing(true);
    setForceRefreshError('');
    setShowForceRefreshConfirm(false);
    try {
      await apiService.forceRefreshCreditData(user.id);
      setForceRefreshToast({ message: "Credit data refresh triggered. Fresh data will be available on the user's next report pull.", type: 'success' });
      setTimeout(() => setForceRefreshToast(null), 5000);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 412) {
          setForceRefreshError('This user has not completed identity verification and cannot be refreshed.');
        } else if (err.status === 404) {
          setForceRefreshError('User not found.');
        } else {
          setForceRefreshToast({ message: 'Refresh failed. The feature may not be enabled for this host, or CRS is unavailable.', type: 'error' });
          setTimeout(() => setForceRefreshToast(null), 5000);
        }
      } else {
        setForceRefreshToast({ message: 'Refresh failed. The feature may not be enabled for this host, or CRS is unavailable.', type: 'error' });
        setTimeout(() => setForceRefreshToast(null), 5000);
      }
    } finally {
      setIsForceRefreshing(false);
    }
  };

  const handleLoadViaMicrofrontend = () => {
    setShowMicrofrontendRequired(false);
    // Navigate to the credit report page (IdentityIQ microfrontend) in the same window
    window.location.href = '/credit-report';
  };

  const refreshFromIdentityIQ = async () => {
    setShowRefreshConfirm(false);
    
    if (!user?.id) {
      setError('User ID not found');
      return;
    }

    try {
      setIsRefreshingFromIIQ(true);
      setError('');
      setShowMicrofrontendRequired(false); // Reset microfrontend state
      
      // Use 3 bureaus if maxPlanEnabled OR revenueCatId is not null, otherwise 1 bureau
      const bureauCount = (user?.maxPlanEnabled || user?.revenueCatId) ? 3 : 1;
      await apiService.refreshUserReport(user.id, bureauCount);
      
      // Trigger refresh of report list for manager view
      if (isManagerView) {
        setReportRefreshTrigger(prev => prev + 1);
        // ReportDateSelector will automatically select and load the latest report
      } else {
        // For regular user view, fetch the updated analytics after a short delay
        setTimeout(() => {
          fetchAnalytics();
        }, 2000);
      }
    } catch (err) {
      // Check if error is 477 (microfrontend required)
      if (err instanceof ApiError && err.status === 477) {
        setShowMicrofrontendRequired(true);
        setError('');
      } else {
        setError(err instanceof ApiError ? err.message : 'Failed to refresh report from IdentityIQ');
      }
    } finally {
      setIsRefreshingFromIIQ(false);
    }
  };

  const copyToClipboard = async () => {
    if (analytics) {
      try {
        await navigator.clipboard.writeText(JSON.stringify(analytics, null, 2));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy to clipboard:', err);
      }
    }
  };

  // Verification flow functions
  const startVerificationFlow = async () => {
    try {
      setIsLoadingVerification(true);
      setVerificationError('');
      setShowVerificationDialog(true);
      
      const response = await apiService.getVerificationQuestions();
      
      if (response.success && response.questions.length === 0) {
        // User is already verified
        setVerificationStep('success');
        // Refetch user info to update enrolled status in context
        await refreshUser();
        // Close dialog and proceed to get report
        setTimeout(() => {
          setShowVerificationDialog(false);
          setRefreshConfirmAction('report');
          setShowRefreshConfirm(true);
        }, 1500);
      } else if (response.success && response.questions.length > 0) {
        // Display verification questions
        setVerificationQuestions(response.questions);
        setSelectedAnswers(new Array(response.questions.length).fill(''));
        setVerificationStep('questions');
      }
    } catch (err) {
      setVerificationError(err instanceof ApiError ? err.message : 'Failed to load verification questions');
    } finally {
      setIsLoadingVerification(false);
    }
  };

  const handleAnswerSelect = (questionIndex: number, answerId: string) => {
    const newAnswers = [...selectedAnswers];
    newAnswers[questionIndex] = answerId;
    setSelectedAnswers(newAnswers);
  };

  const handleSubmitVerification = async () => {
    // Validate all questions are answered
    if (selectedAnswers.some(answer => !answer)) {
      setVerificationError('Please answer all questions');
      return;
    }

    try {
      setVerificationStep('submitting');
      setVerificationError('');
      setIsLoadingVerification(true);
      
      const response = await apiService.submitVerificationAnswers(selectedAnswers);
      
      if (response.status === 'verified') {
        // Success! User is now verified
        setVerificationStep('success');
        
        // Refetch user info to get updated enrolled status in context
        await refreshUser();
        
        // Close dialog and proceed to get report
        setTimeout(() => {
          setShowVerificationDialog(false);
          setRefreshConfirmAction('report');
          setShowRefreshConfirm(true);
        }, 1500);
        
      } else if (response.status === 'additional_question') {
        // More questions needed
        setVerificationQuestions(response.questions || []);
        setSelectedAnswers(new Array(response.questions?.length || 0).fill(''));
        setVerificationStep('questions');
        
      } else if (response.status === 'failed') {
        // Verification failed
        setVerificationError('Verification failed. Incorrect answers provided. Please try again.');
        setVerificationStep('questions');
        
        // Allow user to retry - fetch new questions
        setTimeout(async () => {
          setVerificationError('');
          await startVerificationFlow();
        }, 3000);
      }
    } catch (err) {
      setVerificationError(err instanceof ApiError ? err.message : 'Failed to submit verification answers');
      setVerificationStep('questions');
    } finally {
      setIsLoadingVerification(false);
    }
  };

  // CRS Verification Functions
  const startCRSVerification = async () => {
    setShowCRSVerificationDialog(true);
    setIsCRSVerifying(true);
    setCRSVerificationResult('pending');
    setCRSVerificationMessage('Verifying your identity...');
    setCrsVerificationFieldErrors([]);

    try {
      const response = await apiService.verifyCRSIdentity();
      
      if (response.success && response.idpass) {
        // Verification successful - idpass is true means user is enrolled
        setCRSVerificationResult('success');
        setCRSVerificationMessage(response.message || 'Identity verified successfully!');
        
        // Update user info to reflect enrolled status in context
        await refreshUser();
        
        // Close dialog after a delay and fetch fresh report
        setTimeout(() => {
          setShowCRSVerificationDialog(false);
          fetchAnalytics();
        }, 2000);
      } else {
        // Verification failed - idpass is false
        setCRSVerificationResult('failed');
        setCRSVerificationMessage(response.message || 'Identity verification failed. Please try again later.');
        const fe = response.fieldErrors?.filter(
          (e): e is FieldError =>
            typeof e?.field === 'string' && typeof e?.message === 'string'
        );
        setCrsVerificationFieldErrors(fe?.length ? fe : []);
      }
    } catch (err) {
      setCRSVerificationResult('failed');
      setCRSVerificationMessage(err instanceof ApiError ? err.message : 'Failed to verify identity');
      setCrsVerificationFieldErrors(
        err instanceof ApiError && err.fieldErrors?.length ? err.fieldErrors : []
      );
    } finally {
      setIsCRSVerifying(false);
    }
  };

  const refreshFromCRS = async () => {
    setShowRefreshConfirm(false);

    if (!user?.id) {
      setError('User ID not found');
      return;
    }

    try {
      setIsRefreshingFromIIQ(true);
      setError('');
      
      // Use admin endpoint to refresh CRS report
      const bureauCount = (user?.maxPlanEnabled || user?.revenueCatId) ? 3 : 1;
      await apiService.refreshUserReport(user.id, bureauCount);
      
      // Trigger refresh of report list for manager view
      if (isManagerView) {
        setReportRefreshTrigger(prev => prev + 1);
        // ReportDateSelector will automatically select and load the latest report
      } else {
        // For regular user view, fetch the updated analytics after a short delay
        setTimeout(() => {
          fetchAnalytics();
        }, 2000);
      }
    } catch (err) {
      const errorMessage = err instanceof ApiError ? err.message : 'Failed to refresh CRS report';
      setError(normalizeCRSErrorMessage(errorMessage));
    } finally {
      setIsRefreshingFromIIQ(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.subscription, selectedReportDate, selectedBureauCount]);

  // Background refresh when tab becomes active again
  useEffect(() => {
    if (!isActive || !analytics) return;

    const refreshInBackground = async () => {
      try {
        let data;
        if (isManagerView && user?.id && selectedReportDate && selectedBureauCount) {
          const lang = apiService.getLanguage();
          data = await apiService.getCreditAnalyticsByDate(
            user.id,
            selectedReportDate,
            selectedBureauCount,
            lang !== 'en' ? lang : undefined
          );
        } else if (!isManagerView) {
          const bureauCount = (user?.maxPlanEnabled || user?.revenueCatId) ? 3 : 1;
          data = await apiService.getCreditAnalytics(bureauCount);
        }
        if (data) setAnalytics(data);
      } catch {
        // Silently fail — user still sees previous data
      }
    };
    refreshInBackground();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // Long polling effect for placeholder records
  useEffect(() => {
    // Start polling if the current record is a placeholder (isProcessing = true)
    if (analytics?.isProcessing) {
      console.log('[CreditAnalytics] Starting long polling for placeholder record');
      const intervalId = setInterval(() => {
        console.log('[CreditAnalytics] Polling for updates...');
        fetchAnalytics();
      }, 10000); // Poll every 10 seconds
      
      setPollingIntervalId(intervalId);

      // Cleanup when effect re-runs or component unmounts
      return () => {
        console.log('[CreditAnalytics] Stopping long polling');
        clearInterval(intervalId);
        setPollingIntervalId(null);
      };
    } else {
      // If not processing, make sure any existing polling is stopped
      if (pollingIntervalId) {
        console.log('[CreditAnalytics] Stopping long polling (not processing)');
        clearInterval(pollingIntervalId);
        setPollingIntervalId(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analytics?.isProcessing]);

  const handleReportChange = (reportDate: string | null, analyticsId: string | null, bureauCount: number | null) => {
    setSelectedReportDate(reportDate);
    setSelectedAnalyticsId(analyticsId);
    setSelectedBureauCount(bureauCount);
  };

  const handleReportLoadingChange = (loading: boolean) => {
    setIsLoadingReports(loading);
  };

  // Extract data before early returns
  const creditScores = analytics?.fullReport?.creditScores || [];
  const accounts = React.useMemo(() => analytics?.fullReport?.accountInformation || [], [analytics?.fullReport?.accountInformation]);
  const inquiries = React.useMemo(() => analytics?.fullReport?.creditInquiries || [], [analytics?.fullReport?.creditInquiries]);
  const personalInfo = analytics?.fullReport?.personalInformation || [];
  const publicRecords = analytics?.fullReport?.publicRecords || [];
  const userProvider = String(
    (user as { creditProvider?: string; provider?: string; crsUserId?: string | null } | null)?.creditProvider ||
    (user as { provider?: string } | null)?.provider ||
    ''
  ).toLowerCase();
  const isCRSUser = userProvider === 'crs' || !!(user as { crsUserId?: string | null } | null)?.crsUserId;
  // Handle both old and new creditFactors structure
  const creditFactors = analytics && Array.isArray(analytics.creditFactors)
    ? analytics.creditFactors
    : (analytics?.creditFactors as { factors: CreditFactor[] })?.factors || [];

  // Sorting logic - must be defined before any early returns
  const handleAccountSort = (field: AccountSortField) => {
    setAccountSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleInquirySort = (field: InquirySortField) => {
    setInquirySort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const downloadAnalyticsPdf = async () => {
    if (!user?.id) {
      setPdfError('User ID not found');
      return;
    }

    try {
      setIsDownloadingPdf(true);
      setPdfError('');

      // Keep bureau selection aligned with analytics report scope.
      const bureau = (selectedBureauCount === 1 || selectedBureauCount === 3)
        ? selectedBureauCount
        : ((user?.maxPlanEnabled || user?.revenueCatId) ? 3 : 1);

      const blob = await apiService.getCreditReportPdf(bureau as 1 | 3, 'attachment');
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `credit_report_${user.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setShowPdfConfirm(false);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to download credit report PDF';
      setPdfError(normalizeCRSErrorMessage(message));
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  // Group accounts by ID and get bureau variants
  const accountsGroupedById = React.useMemo(() => {
    const grouped = new Map<string, AccountInformation[]>();
    accounts.forEach(account => {
      if (!grouped.has(account.id)) {
        grouped.set(account.id, []);
      }
      grouped.get(account.id)!.push(account);
    });
    return grouped;
  }, [accounts]);

  const sortedAccounts = React.useMemo(() => {
    // Get unique account IDs and use Equifax data for display (fallback to first available)
    const uniqueAccounts = Array.from(accountsGroupedById.entries()).map(([id, bureauAccounts]) => {
      // Prefer Equifax data for main row display
      const equifaxAccount = bureauAccounts.find(acc => acc.bureau === 'Equifax');
      return equifaxAccount || bureauAccounts[0];
    });

    const getHealthPriority = (account: AccountInformation): number => {
      const accountVariants = accountsGroupedById.get(account.id) || [account];
      const hasLateInAnyBureau = accountVariants.some(variant => variant.isLate === true);
      const hasNegativeInAnyBureau = accountVariants.some(variant => variant.negativeAccount === true);

      // Requested ordering: Lates -> Negative -> Healthy.
      if (hasLateInAnyBureau) return 0;
      if (hasNegativeInAnyBureau) return 1;
      return 2;
    };
    
    // Then filter by status
    let filtered = uniqueAccounts;
    if (accountStatusFilter !== 'all') {
      filtered = uniqueAccounts.filter(account => {
        const accountVariants = accountsGroupedById.get(account.id) || [account];
        const hasNegativeInAnyBureau = accountVariants.some(variant => variant.negativeAccount === true);
        const hasLateInAnyBureau = accountVariants.some(variant => variant.isLate === true);
        switch (accountStatusFilter) {
          case 'needs-work':
            return hasNegativeInAnyBureau || hasLateInAnyBureau;
          case 'healthy':
            return !hasNegativeInAnyBureau && !hasLateInAnyBureau;
          default:
            return true;
        }
      });
    }
    
    // Default ordering: Lates -> Negative -> Healthy
    if (!accountSort.field || !accountSort.direction) {
      return [...filtered].sort((a, b) => getHealthPriority(a) - getHealthPriority(b));
    }
    
    return [...filtered].sort((a, b) => {
      const healthPriorityDiff = getHealthPriority(a) - getHealthPriority(b);
      if (healthPriorityDiff !== 0) {
        return healthPriorityDiff;
      }

      const multiplier = accountSort.direction === 'asc' ? 1 : -1;
      const aVal = a[accountSort.field as keyof AccountInformation];
      const bVal = b[accountSort.field as keyof AccountInformation];
      
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return aVal.localeCompare(bVal) * multiplier;
      }
      return 0;
    });
  }, [accountsGroupedById, accountSort, accountStatusFilter]);

  const sortedInquiries = React.useMemo(() => {
    // First apply bureau filter
    let filtered = inquiries;
    if (inquiryBureauFilter !== 'all') {
      filtered = inquiries.filter(inquiry => {
        const bureauLower = inquiry.bureau.toLowerCase();
        switch (inquiryBureauFilter) {
          case 'transunion':
            return bureauLower.includes('transunion');
          case 'experian':
            return bureauLower.includes('experian');
          case 'equifax':
            return bureauLower.includes('equifax');
          default:
            return true;
        }
      });
    }
    
    // Then apply sorting
    if (!inquirySort.field || !inquirySort.direction) return filtered;
    
    return [...filtered].sort((a, b) => {
      const multiplier = inquirySort.direction === 'asc' ? 1 : -1;
      const aVal = a[inquirySort.field as keyof CreditInquiry];
      const bVal = b[inquirySort.field as keyof CreditInquiry];
      
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return aVal.localeCompare(bVal) * multiplier;
      }
      return 0;
    });
  }, [inquiries, inquirySort, inquiryBureauFilter]);



  // Show inline content spinner when switching between reports (after analytics data exists)
  const showContentLoadingSpinner = isManagerView && isLoading && analytics && !isLoadingReports;

  return (
    <div className="space-y-0">
      {/* Page Title */}
      <h1 className="text-2xl font-bold text-gray-900 mb-4">Analytics</h1>
      
      {/* Report Date Selector - Always shown */}
      {user?.id && (
        <div style={{ display: isLoadingReports ? 'none' : 'block' }}>
          <ReportDateSelector
            key={user.id}
            userId={user.id}
            onReportChange={handleReportChange}
            selectedDate={selectedReportDate}
            onLoadingChange={handleReportLoadingChange}
            refreshTrigger={reportRefreshTrigger}
            onRefresh={handleRefreshCurrentReport}
            isRefreshing={isRefreshingFromIIQ || isLoading}
            rightActions={
              isCRSUser ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => { setForceRefreshError(''); setShowForceRefreshConfirm(true); }}
                    disabled={isForceRefreshing}
                    className="min-h-[40px] px-6 py-2.5 flex items-center gap-2 border-gray-300 rounded-lg shadow-sm"
                    title="Force a fresh pull of credit data from CRS"
                  >
                    {isForceRefreshing ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm font-medium">Refreshing...</span>
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4" />
                        <span className="text-sm font-medium">Force Refresh from CRS</span>
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowPdfConfirm(true)}
                    disabled={isDownloadingPdf}
                    className="min-h-[40px] px-6 py-2.5 flex items-center gap-2 border-gray-300 rounded-lg shadow-sm"
                  >
                    {isDownloadingPdf ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm font-medium">Requesting...</span>
                      </>
                    ) : (
                      <>
                        <FileText className="h-4 w-4" />
                        <span className="text-sm font-medium">Request Bureau PDF</span>
                      </>
                    )}
                  </Button>
                </>
              ) : null
            }
          />
        </div>
      )}
      {pdfError && (
        <p className="text-sm text-red-600 mb-2">{pdfError}</p>
      )}

      {/* Main content - always shown */}
      <>

      <Tabs defaultValue="overview" className="w-full">
        <div className="mt-4 mb-8">
          <TabsList className="flex bg-gray-100 p-1 rounded-xl w-full">
            <TabsTrigger value="overview" className="flex-1 px-2.5 py-1.5 text-sm font-medium rounded-[10px] data-[state=active]:bg-white data-[state=active]:shadow-sm">Overview</TabsTrigger>
            <TabsTrigger value="history" className="flex-1 px-2.5 py-1.5 text-sm font-medium rounded-[10px] data-[state=active]:bg-white data-[state=active]:shadow-sm">History</TabsTrigger>
            <TabsTrigger value="accounts" className="flex-1 px-2.5 py-1.5 text-sm font-medium rounded-[10px] data-[state=active]:bg-white data-[state=active]:shadow-sm">Accounts</TabsTrigger>
            <TabsTrigger value="inquiries" className="flex-1 px-2.5 py-1.5 text-sm font-medium rounded-[10px] data-[state=active]:bg-white data-[state=active]:shadow-sm">Inquiries</TabsTrigger>
            <TabsTrigger value="personal" className="flex-1 px-2.5 py-1.5 text-sm font-medium rounded-[10px] data-[state=active]:bg-white data-[state=active]:shadow-sm">Personal Info</TabsTrigger>
            <TabsTrigger value="roadmap" className="flex-1 px-2.5 py-1.5 text-sm font-medium rounded-[10px] data-[state=active]:bg-white data-[state=active]:shadow-sm">Roadmap</TabsTrigger>
          </TabsList>
        </div>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6 mt-6 relative">
          {showContentLoadingSpinner && (
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 flex items-center justify-center min-h-[400px] transition-opacity duration-200">
              <div className="text-center space-y-4">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                <p className="text-sm text-muted-foreground">Loading report...</p>
              </div>
            </div>
          )}
          <div
            className={`transition-all duration-500 ease-out ${
              showContentLoadingSpinner
                ? 'opacity-50 translate-y-2'
                : 'opacity-100 translate-y-0'
            }`}
            style={{
              animation: showContentLoadingSpinner ? 'none' : 'fadeInUp 0.6s ease-out'
            }}
          >
            {renderOverviewTab()}
          </div>
        </TabsContent>

        {/* Full Report Tab */}
        <TabsContent value="full-report" className="mt-6 relative">
          {showContentLoadingSpinner && (
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 flex items-center justify-center min-h-[400px] transition-opacity duration-200">
              <div className="text-center space-y-4">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                <p className="text-sm text-muted-foreground">Loading report...</p>
              </div>
            </div>
          )}
          <div className={`transition-opacity duration-200 ${showContentLoadingSpinner ? 'opacity-50' : 'opacity-100'}`}>
          {analytics && analytics.fullReport?.creditScores && analytics.fullReport.creditScores.length > 0 && (
            <>
              <CreditReportView analytics={analytics} />
              
              {/* Raw Data Link with Popup */}
              <div className="mt-8 flex justify-center">
                <Dialog>
                  <DialogTrigger asChild>
                    <button className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4 transition-colors">
                      View raw JSON data
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl max-h-[80vh]">
                    <DialogHeader>
                      <DialogTitle>Raw JSON Data</DialogTitle>
                      <DialogDescription>
                        Complete analytics response in JSON format
                      </DialogDescription>
                    </DialogHeader>
                    <div className="flex items-center justify-end mb-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={copyToClipboard}
                        className="flex items-center space-x-2"
                      >
                        {copied ? (
                          <Check className="h-4 w-4 text-foreground" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                        <span>{copied ? 'Copied!' : 'Copy'}</span>
                      </Button>
                    </div>
                    <pre className="text-xs bg-muted p-4 rounded-md overflow-auto max-h-[calc(80vh-200px)]">
                      {JSON.stringify(analytics, null, 2)}
                    </pre>
                  </DialogContent>
                </Dialog>
              </div>
            </>
          )}
          </div>
        </TabsContent>

        {/* Credit History Tab */}
        <TabsContent value="history" className="mt-6 relative">
          {showContentLoadingSpinner && (
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 flex items-center justify-center min-h-[400px] transition-opacity duration-200">
              <div className="text-center space-y-4">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                <p className="text-sm text-muted-foreground">Loading report...</p>
              </div>
            </div>
          )}
          <div className={`transition-opacity duration-200 ${showContentLoadingSpinner ? 'opacity-50' : 'opacity-100'}`}>
            <CreditHistory />
          </div>
        </TabsContent>

        {/* Accounts Tab */}
        <TabsContent value="accounts" className="mt-6 relative">
          {showContentLoadingSpinner && (
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 flex items-center justify-center min-h-[400px] transition-opacity duration-200">
              <div className="text-center space-y-4">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                <p className="text-sm text-muted-foreground">Loading report...</p>
              </div>
            </div>
          )}
          <div className={`transition-opacity duration-200 ${showContentLoadingSpinner ? 'opacity-50' : 'opacity-100'}`}>
            {renderAccountsTab()}
          </div>
        </TabsContent>

        {/* Inquiries Tab */}
        <TabsContent value="inquiries" className="mt-6 relative">
          {showContentLoadingSpinner && (
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 flex items-center justify-center min-h-[400px] transition-opacity duration-200">
              <div className="text-center space-y-4">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                <p className="text-sm text-muted-foreground">Loading report...</p>
              </div>
            </div>
          )}
          <div className={`transition-opacity duration-200 ${showContentLoadingSpinner ? 'opacity-50' : 'opacity-100'}`}>
            {renderInquiriesTab()}
          </div>
        </TabsContent>

        {/* Personal Info Tab */}
        <TabsContent value="personal" className="mt-6 relative">
          {showContentLoadingSpinner && (
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 flex items-center justify-center min-h-[400px] transition-opacity duration-200">
              <div className="text-center space-y-4">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                <p className="text-sm text-muted-foreground">Loading report...</p>
              </div>
            </div>
          )}
          <div className={`transition-opacity duration-200 ${showContentLoadingSpinner ? 'opacity-50' : 'opacity-100'}`}>
            {renderPersonalInfoTab()}
          </div>
        </TabsContent>

        {/* Roadmap Tab */}
        <TabsContent value="roadmap" className="mt-6 relative">
          {showContentLoadingSpinner && (
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 flex items-center justify-center min-h-[400px] transition-opacity duration-200">
              <div className="text-center space-y-4">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                <p className="text-sm text-muted-foreground">Loading report...</p>
              </div>
            </div>
          )}
          <div className={`transition-opacity duration-200 ${showContentLoadingSpinner ? 'opacity-50' : 'opacity-100'}`}>
            <CreditRoadmap
              analyticsId={selectedAnalyticsId || analytics?.id || null}
              accounts={analytics?.fullReport?.accountInformation}
              inquiries={analytics?.fullReport?.creditInquiries}
            />
          </div>
        </TabsContent>

        {/* Voice Helper Tab - Temporarily hidden */}
        {/* <TabsContent value="voice" className="mt-6">
          <VoiceHelper />
        </TabsContent> */}
      </Tabs>

      {/* Verification Dialog */}
      <Dialog open={showVerificationDialog} onOpenChange={setShowVerificationDialog}>
        <DialogContent className="max-w-2xl">
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Identity Verification Required</h2>
              <p className="text-sm text-gray-600 mt-2">
                {verificationStep === 'questions' && 'Please answer the following questions to verify your identity.'}
                {verificationStep === 'submitting' && 'Verifying your identity...'}
                {verificationStep === 'success' && 'Identity verified successfully!'}
              </p>
            </div>

          {isLoadingVerification && verificationStep === 'questions' && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          )}

          {verificationError && (
            <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
              {verificationError}
            </div>
          )}

          {verificationStep === 'questions' && !isLoadingVerification && verificationQuestions.length > 0 && (
            <div className="space-y-6 max-h-[60vh] overflow-y-auto">
              {verificationQuestions.map((question, qIndex) => (
                <div key={qIndex} className="space-y-3">
                  <div className="font-medium text-sm">
                    Question {qIndex + 1} of {verificationQuestions.length}
                  </div>
                  <div className="text-base mb-3">{question.question}</div>
                  <div className="space-y-2">
                    {question.answers.map((answer) => (
                      <label
                        key={answer.id}
                        className="flex items-center space-x-3 p-3 border rounded-md hover:bg-accent cursor-pointer transition-colors"
                      >
                        <input
                          type="radio"
                          name={`question-${qIndex}`}
                          value={answer.id}
                          checked={selectedAnswers[qIndex] === answer.id}
                          onChange={() => handleAnswerSelect(qIndex, answer.id)}
                          className="h-4 w-4"
                        />
                        <span className="text-sm">{answer.answer}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {verificationStep === 'submitting' && (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Verifying your identity...</p>
            </div>
          )}

          {verificationStep === 'success' && (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <Check className="h-12 w-12 text-green-600" />
              <p className="text-lg font-medium">Identity Verified Successfully!</p>
              <p className="text-sm text-muted-foreground">Proceeding to fetch your credit report...</p>
            </div>
          )}

          {verificationStep === 'questions' && !isLoadingVerification && (
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowVerificationDialog(false)}
                disabled={isLoadingVerification}
                className="px-4"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitVerification}
                disabled={isLoadingVerification || selectedAnswers.some(a => !a)}
                className="bg-black hover:bg-black/90 text-white px-4"
              >
                {isLoadingVerification ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Answers'
                )}
              </Button>
            </div>
          )}
          </div>
        </DialogContent>
      </Dialog>

      {/* CRS Identity Verification Dialog */}
      <Dialog open={showCRSVerificationDialog} onOpenChange={setShowCRSVerificationDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">CRS Identity Verification</h2>
              <p className="text-sm text-gray-600 mt-2">
                {crsVerificationResult === 'pending' && 'Verifying your identity with CRS...'}
                {crsVerificationResult === 'success' && 'Identity verified successfully!'}
                {crsVerificationResult === 'failed' && 'Identity verification failed'}
              </p>
            </div>

          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            {crsVerificationResult === 'pending' && (
              <>
                <Loader2 className="h-12 w-12 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">{crsVerificationMessage}</p>
              </>
            )}

            {crsVerificationResult === 'success' && (
              <>
                <Check className="h-12 w-12 text-green-600" />
                <p className="text-lg font-medium">Verification Successful!</p>
                <p className="text-sm text-muted-foreground">{crsVerificationMessage}</p>
              </>
            )}

            {crsVerificationResult === 'failed' && (
              <>
                <div className="h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <span className="text-2xl text-red-600 dark:text-red-400">✕</span>
                </div>
                <p className="text-lg font-medium text-red-600 dark:text-red-400">Verification Failed</p>
                <div className="text-sm text-muted-foreground text-center max-w-md space-y-2">
                  <p>{crsVerificationMessage}</p>
                  {crsVerificationFieldErrors.length > 0 && (
                    <ul className="text-left list-disc pl-5 text-xs text-red-700 dark:text-red-300 space-y-1 border-t border-red-200 dark:border-red-800 pt-2 mt-2">
                      {crsVerificationFieldErrors.map((fe, idx) => (
                        <li key={`${fe.field}-${idx}`}>
                          <span className="font-medium">{fe.field}</span>
                          {': '}
                          {fe.message}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </div>

          {crsVerificationResult === 'failed' && (
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowCRSVerificationDialog(false)}
                className="px-4"
              >
                Close
              </Button>
              <Button
                onClick={startCRSVerification}
                disabled={isCRSVerifying}
                className="bg-black hover:bg-black/90 text-white px-4"
              >
                {isCRSVerifying ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Retrying...
                  </>
                ) : (
                  'Retry'
                )}
              </Button>
            </div>
          )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog for Getting Latest Report / Verify Identity */}
      <Dialog open={showRefreshConfirm} onOpenChange={setShowRefreshConfirm}>
        <DialogContent className="sm:max-w-[500px]">
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {refreshConfirmAction === 'verify' ? 'Verify Identity' : 'Get Latest Credit Report'}
              </h2>
              <p className="text-sm text-gray-600 mt-2">
                {refreshConfirmAction === 'verify'
                  ? 'Are you sure you want to start identity verification? This will send a verification request to confirm the user\'s identity before they can access credit reports.'
                  : 'Are you sure you want to send a request to the bureau to fetch the latest credit report? This will retrieve fresh data from the credit bureaus.'}
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowRefreshConfirm(false)}
                className="px-4"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setShowRefreshConfirm(false);
                  if (refreshConfirmAction === 'verify') {
                    const isCRS = user?.creditProvider === 'crs' || !!user?.crsUserId;
                    if (isCRS) {
                      startCRSVerification();
                    } else {
                      startVerificationFlow();
                    }
                  } else {
                    const isCRS = user?.creditProvider === 'crs' || !!user?.crsUserId;
                    if (isCRS) {
                      refreshFromCRS();
                    } else {
                      refreshFromIdentityIQ();
                    }
                  }
                }}
                disabled={isRefreshingFromIIQ}
                className="bg-black hover:bg-black/90 text-white px-4"
              >
                {isRefreshingFromIIQ ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {refreshConfirmAction === 'verify' ? 'Verifying...' : 'Getting Report...'}
                  </>
                ) : (
                  <>
                    {refreshConfirmAction === 'verify'
                      ? <ShieldCheck className="h-4 w-4 mr-2" />
                      : <Download className="h-4 w-4 mr-2" />}
                    Confirm
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog for Analytics PDF Download */}
      {/* Force Refresh Confirmation Dialog */}
      <Dialog open={showForceRefreshConfirm} onOpenChange={setShowForceRefreshConfirm}>
        <DialogContent className="sm:max-w-[500px]">
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Force Refresh Credit Data?</h2>
              <p className="text-sm text-gray-600 mt-2">
                This will remove and re-enroll the user's score and report features in CRS. Their next data request will return fresh credit data.
              </p>
              {forceRefreshError && (
                <p className="text-sm text-red-600 mt-2">{forceRefreshError}</p>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowForceRefreshConfirm(false)} className="px-4">
                Cancel
              </Button>
              <Button onClick={handleForceRefresh} className="bg-black hover:bg-black/90 text-white px-4">
                <RefreshCw className="h-4 w-4 mr-2" />
                Continue
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Force Refresh Toast */}
      {forceRefreshToast && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2 pointer-events-none ${forceRefreshToast.type === 'success' ? 'bg-gray-900 text-white' : 'bg-red-700 text-white'}`}>
          {forceRefreshToast.type === 'success' ? (
            <Check className="h-4 w-4 text-green-400 flex-shrink-0" />
          ) : (
            <CircleX className="h-4 w-4 text-red-300 flex-shrink-0" />
          )}
          {forceRefreshToast.message}
        </div>
      )}

      {/* Confirmation Dialog for Analytics PDF Download */}
      <Dialog open={showPdfConfirm} onOpenChange={setShowPdfConfirm}>
        <DialogContent className="sm:max-w-[500px]">
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Download Latest Credit Report PDF</h2>
              <p className="text-sm text-gray-600 mt-2">
                Do you want to download the latest credit report as a PDF file?
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowPdfConfirm(false)}
                className="px-4"
                disabled={isDownloadingPdf}
              >
                Cancel
              </Button>
              <Button
                onClick={downloadAnalyticsPdf}
                disabled={isDownloadingPdf}
                className="bg-black hover:bg-black/90 text-white px-4"
              >
                {isDownloadingPdf ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Confirm
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Microfrontend Required Dialog (477 error) */}
      <Dialog open={showMicrofrontendRequired} onOpenChange={setShowMicrofrontendRequired}>
        <DialogContent className="sm:max-w-[500px]">
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">3-Bureau Credit Report Required</h2>
              <p className="text-sm text-gray-600 mt-2">
                The credit bureau requires loading your full 3-bureau credit report through an interactive interface.
                This operation is not free and will consume credits.
              </p>
              <p className="text-sm text-gray-600 mt-2">
                The report will load in this window where you can view and process your complete credit report
                from all three bureaus (TransUnion, Experian, and Equifax).
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowMicrofrontendRequired(false)}
                className="px-4"
              >
                Cancel
              </Button>
              <Button
                onClick={handleLoadViaMicrofrontend}
                className="bg-black hover:bg-black/90 text-white px-4"
              >
                <FileText className="h-4 w-4 mr-2" />
                Load via Microfrontend
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      </>
    </div>
  );

  function renderOverviewTab() {
    // Show error if there's an error
    if (error) {
      return (
        <div className="space-y-6">
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <p className="text-destructive">{error}</p>
                <Button onClick={fetchAnalytics} variant="outline">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry
                </Button>
              </div>
            </CardContent>
          </Card>
          <div className="text-center py-4">
            <p className="text-muted-foreground text-sm">
              Or click "Get Latest Report" above to fetch fresh credit data.
            </p>
          </div>
          {user?.id && (
            <div className="mt-8">
              <UserNotes userId={user.id} />
            </div>
          )}
        </div>
      );
    }

    // Show skeleton when no data (keep showing even during refresh until we have data)
    if (!analytics) {
      const isLoadingData = isRefreshingFromIIQ || isLoading;
      return (
        <div className="space-y-6">
          {/* Skeleton Credit Scores */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className={isLoadingData ? "animate-pulse" : ""}>
                <CardHeader className="pb-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-full bg-muted"></div>
                    <div className="h-4 w-24 bg-muted rounded"></div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="h-8 w-16 bg-muted rounded mb-2"></div>
                  <div className="h-3 w-32 bg-muted rounded"></div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Skeleton Credit Factors */}
          {[1, 2, 3].map((i) => (
            <Card key={i} className={isLoadingData ? "animate-pulse" : ""}>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 rounded-full bg-muted"></div>
                      <div className="space-y-2">
                        <div className="h-5 w-48 bg-muted rounded"></div>
                        <div className="h-4 w-32 bg-muted rounded"></div>
                      </div>
                    </div>
                    <div className="h-8 w-16 bg-muted rounded"></div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-4 w-32 bg-muted rounded"></div>
                    <div className="h-4 w-full bg-muted rounded"></div>
                    <div className="h-4 w-3/4 bg-muted rounded"></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Message */}
          <div className="text-center py-8">
            <p className="text-muted-foreground">
              {isLoadingData
                ? 'Fetching your credit report...'
                : 'No credit report data available. Click "Get Latest Report" to fetch your credit information.'}
            </p>
          </div>
          {user?.id && (
            <div className="mt-2">
              <UserNotes userId={user.id} />
            </div>
          )}
        </div>
      );
    }

    return (
      <>

      {/* Refresh Recommended Hint */}
      {analytics?.needsRefresh && !analytics?.isProcessing && (
        <div className="text-orange-600 dark:text-orange-400 text-xs font-medium mb-3">
          Outdated credit report.
        </div>
      )}

      {/* Credit Scores - At the top */}
      {creditScores.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {creditScores.map((score, index) => {
            // Get bureau abbreviation
            const getBureauAbbr = (bureau: string) => {
              if (bureau.includes('TransUnion')) return 'TU';
              if (bureau.includes('Equifax')) return 'EQ';
              if (bureau.includes('Experian')) return 'EX';
              return bureau.substring(0, 2).toUpperCase();
            };
            
            // Find delta for this bureau
            const delta = scoreDeltas?.deltas?.find((d: any) =>
              d.bureau.toLowerCase() === score.bureau.toLowerCase() ||
              d.bureau.toLowerCase().includes(score.bureau.toLowerCase().substring(0, 3))
            );
            
            return (
            <Card
              key={index}
              className="border border-gray-200 rounded-xl shadow-sm overflow-hidden"
              style={{
                animation: `fadeInUp 0.5s ease-out ${index * 0.1}s both`
              }}
            >
              <CardContent className="p-6">
                {/* Header with Bureau Name */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-gray-700">
                        {getBureauAbbr(score.bureau)}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{score.bureau}</span>
                  </div>
                  {delta && delta.delta !== 0 && (
                    <div className={`flex items-center gap-1 ${delta.delta > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {delta.delta > 0 ? (
                        <TrendingUp className="h-4 w-4" strokeWidth={2} />
                      ) : (
                        <TrendingDown className="h-4 w-4" strokeWidth={2} />
                      )}
                      <span className="text-sm font-semibold">
                        {delta.delta > 0 ? '+' : ''}{delta.delta}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Large Score Display */}
                <div className="text-4xl font-bold text-gray-900 mb-2">
                  {score.score}
                </div>
                
                {/* Report Date */}
                <p className="text-xs text-gray-500">
                  Reported: {score.dateReported ? formatDate(score.dateReported) : formatDate(analytics.reportDate)}
                </p>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}

      {/* Public Records */}
      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white mb-8">
        {publicRecords.length > 0 ? (
          <Table>
            <TableHeader className="[&_tr]:border-b-0">
              <TableRow className="border-b-0">
                <TableHead colSpan={8} className="px-4 py-3">
                  <h2 className="text-xl font-semibold text-gray-900">Public Records</h2>
                </TableHead>
              </TableRow>
              <TableRow className="border-b border-b-gray-200 border-t-0">
                <TableHead className="pl-4 pr-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>Type</TableHead>
                <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>Status</TableHead>
                <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>Filing Date</TableHead>
                <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>Resolved Date</TableHead>
                <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>Amount</TableHead>
                <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>Bureau</TableHead>
                <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>Court Name</TableHead>
                <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>Reference #</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {publicRecords.map((record: PublicRecord, index: number) => (
                <TableRow key={`${record.bureau}-${record.type}-${record.referenceNumber || index}`} className="border-b border-gray-200 last:border-b-0 hover:bg-gray-50/50 transition-colors">
                  <TableCell className="pl-4 pr-2 py-3 text-gray-900">{record.type || '-'}</TableCell>
                  <TableCell className="px-2 py-3 text-gray-900">{record.status || '-'}</TableCell>
                  <TableCell className="px-2 py-3 text-gray-900">{record.filingDate ? formatDate(record.filingDate) : '-'}</TableCell>
                  <TableCell className="px-2 py-3 text-gray-900">{record.resolvedDate ? formatDate(record.resolvedDate) : '-'}</TableCell>
                  <TableCell className="px-2 py-3 text-gray-900">{record.amount || '-'}</TableCell>
                  <TableCell className="px-2 py-3 text-gray-900">{record.bureau || '-'}</TableCell>
                  <TableCell className="px-2 py-3 text-gray-900">{record.courtName || '-'}</TableCell>
                  <TableCell className="px-2 py-3 text-gray-900">{record.referenceNumber || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="px-4 py-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Public Records</h2>
            <p className="text-sm text-gray-500">No public records found.</p>
          </div>
        )}
      </div>

      {/* Credit Factors */}
      {creditFactors.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white mb-8">
          <Table>
            <TableHeader className="[&_tr]:border-b-0">
              {/* Header Row - Title */}
              <TableRow className="border-b-0">
                <TableHead colSpan={6} className="px-4 py-3">
                  <div className="flex flex-col gap-1">
                    <h2 className="text-xl font-semibold text-gray-900">Bureau Comparison</h2>
                    <p className="text-sm text-gray-500 font-normal">Side-by-side comparison of key metrics across all three bureaus</p>
                  </div>
                </TableHead>
              </TableRow>
              
              {/* Column Headers */}
              <TableRow className="border-b border-b-gray-200 border-t-0">
                <TableHead className="pl-4 pr-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                  Metric
                </TableHead>
                <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }} />
                <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                  Status
                </TableHead>
                <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                  TransUnion
                </TableHead>
                <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                  Experian
                </TableHead>
                <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                  Equifax
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {creditFactors.map((factor: CreditFactor, index: number) => {
                // Helper to get rating color based on original rating
                const getRatingColorClass = (rating: string): string => {
                  const lower = rating.toLowerCase();
                  if (lower === 'excellent' || lower === 'good') {
                    return 'text-green-600 border-green-200 bg-green-50';
                  } else if (lower === 'fair') {
                    return 'text-orange-600 border-orange-200 bg-orange-50';
                  } else if (lower === 'poor' || lower === 'needs work') {
                    return 'text-red-600 border-red-200 bg-red-50';
                  }
                  return 'text-gray-600 border-gray-200 bg-gray-50';
                };

                // Helper function to format bureau values based on factor type
                const formatBureauValue = (value: any, factorId: string): string => {
                  if (value === null || value === undefined) return '-';
                  const stringValue = String(value);
                  
                  // Payment History and Credit Utilization should be percentages
                  if (factorId === 'Payment History' || factorId === 'Credit Utilization') {
                    // If it's a number, add percentage sign
                    const numValue = Number(value);
                    if (!isNaN(numValue)) {
                      return `${numValue}%`;
                    }
                    // If already has %, return as is
                    if (stringValue.includes('%')) {
                      return stringValue;
                    }
                    // Otherwise add %
                    return `${value}%`;
                  }
                  
                  // Credit Age should be formatted as years and months
                  if (factorId === 'Credit Age') {
                    // Try to parse as a number first
                    const numValue = Number(value);
                    
                    // If it's a valid number, format it from decimal years (e.g., 8.6 years)
                    if (!isNaN(numValue)) {
                      const years = Math.floor(numValue);
                      const months = Math.round((numValue - years) * 12);
                      
                      if (years === 0) {
                        return `${months} m`;
                      } else if (months === 0) {
                        return `${years}y`;
                      } else {
                        return `${years}y ${months}m`;
                      }
                    }
                    
                    // If not a number, return as is (might already be formatted)
                    return stringValue;
                  }
                  
                  // Default: return value as is
                  return stringValue;
                };

                const getInquiriesTotalValue = (bureauValues?: Record<string, any>): string => {
                  if (!bureauValues) return '-';
                  const numbers = ['TransUnion', 'Experian', 'Equifax']
                    .map((bureau) => {
                      const raw = bureauValues[bureau];
                      if (raw === null || raw === undefined || raw === '-') return NaN;
                      const numeric = Number.parseFloat(String(raw).replace(/[^\d.-]/g, ''));
                      return Number.isFinite(numeric) ? numeric : NaN;
                    })
                    .filter((value) => Number.isFinite(value));

                  if (numbers.length === 0) return '-';
                  return String(numbers.reduce((sum, value) => sum + value, 0));
                };

                const metricValue =
                  factor.title.toLowerCase().includes('inquir')
                    ? getInquiriesTotalValue(factor.bureauValues as Record<string, any> | undefined)
                    : factor.value;

                return (
                <TableRow
                  key={factor.id}
                  className={`border-b border-gray-200 ${index === 0 ? 'border-t border-t-gray-200' : ''} last:border-b-0 hover:bg-gray-50/50 transition-colors`}
                >
                  <TableCell className="pl-4 pr-2 py-3 font-medium text-gray-900">
                    {factor.title}
                  </TableCell>
                  <TableCell className="px-2 py-3 text-gray-900 font-semibold">
                    {metricValue}
                  </TableCell>
                  <TableCell className="px-2 py-3">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-lg border ${getRatingColorClass(factor.rating)}`}>
                      {factor.rating}
                    </span>
                  </TableCell>
                  <TableCell className="px-2 py-3 text-gray-900">
                    {formatBureauValue(factor.bureauValues?.TransUnion, factor.title)}
                  </TableCell>
                  <TableCell className="px-2 py-3 text-gray-900">
                    {formatBureauValue(factor.bureauValues?.Experian, factor.title)}
                  </TableCell>
                  <TableCell className="px-2 py-3 text-gray-900">
                    {formatBureauValue(factor.bureauValues?.Equifax, factor.title)}
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Recommendations with Tabs */}
      {(analytics?.isProcessing || analytics?.mainAlert || creditFactors.some((f: CreditFactor) => f.alerts && f.alerts.length > 0)) && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Recommendations to Improve Score</h2>

          {analytics?.isProcessing ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-2">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Working on AI recommendations...</span>
                </div>
                {analytics.message && (
                  <p className="text-sm text-gray-500">{analytics.message}</p>
                )}
              </div>
            </div>
          ) : (
            <Tabs defaultValue="main" className="w-full">
              <TabsList className="flex flex-wrap justify-start bg-transparent p-0 gap-2 mb-4 h-auto">
                {analytics?.mainAlert && (
                  <TabsTrigger
                    value="main"
                    className="px-3 py-1.5 text-sm font-medium rounded-md bg-white data-[state=active]:bg-gray-100 whitespace-nowrap"
                  >
                    Main
                  </TabsTrigger>
                )}
                {creditFactors.map((factor: CreditFactor) => {
                  if (!factor.alerts || factor.alerts.length === 0) return null;
                  return (
                    <TabsTrigger
                      key={factor.id}
                      value={factor.id}
                      className="px-3 py-1.5 text-sm font-medium rounded-md bg-white data-[state=active]:bg-gray-100 whitespace-nowrap"
                    >
                      {factor.title}
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {/* Main Alert Tab */}
              {analytics?.mainAlert && (
                <TabsContent value="main" className="mt-0">
                  <div className="bg-[#F9FAFB] border-l-4 border-l-purple-600 p-4 rounded-lg">
                    <h3 className="text-base font-semibold text-gray-900 mb-2">{analytics.mainAlert.title}</h3>
                    <p className="text-sm text-gray-900 mb-2">{analytics.mainAlert.recommendation}</p>
                    <p className="text-sm text-gray-500">{analytics.mainAlert.explanation}</p>
                    {analytics.mainAlert.marketplaceButtonText && (
                      <Button className="mt-4" size="sm">
                        {analytics.mainAlert.marketplaceButtonText}
                      </Button>
                    )}
                  </div>
                </TabsContent>
              )}

              {/* Credit Factor Alerts Tabs */}
              {creditFactors.map((factor: CreditFactor) => {
                if (!factor.alerts || factor.alerts.length === 0) return null;

                return (
                  <TabsContent key={factor.id} value={factor.id} className="mt-0">
                    <div className="space-y-3">
                      {factor.alerts.map((alert: any, index: number) => (
                        <div key={index} className="bg-[#F9FAFB] border-l-4 border-l-purple-600 p-4 rounded-lg">
                          <h3 className="text-base font-semibold text-gray-900 mb-2">{alert.title}</h3>
                          <p className="text-sm text-gray-900 mb-2">{alert.recommendation}</p>
                          <p className="text-sm text-gray-500">{alert.explanation}</p>
                        </div>
                      ))}
                    </div>
                  </TabsContent>
                );
              })}
            </Tabs>
          )}
        </div>
      )}

      {/* Notes & Handoffs */}
      {user?.id && (
        <div className="mt-8">
          <UserNotes userId={user.id} />
        </div>
      )}

      </>
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function renderFactorsTab() {
    // Helper to get status badge color based on consistency across bureaus
    const getStatusColorClass = (bureauValues: any): string => {
      if (!bureauValues) return 'text-gray-600 border-gray-200 bg-gray-50';
      
      const values = Object.values(bureauValues).filter(v => v !== null && v !== undefined && v !== '-');
      if (values.length === 0) return 'text-gray-600 border-gray-200 bg-gray-50';
      
      const uniqueValues = new Set(values);
      if (uniqueValues.size === 1) {
        return 'text-green-600 border-green-200 bg-green-50';
      } else {
        return 'text-orange-600 border-orange-200 bg-orange-50';
      }
    };

    const getStatusLabel = (bureauValues: any): string => {
      if (!bureauValues) return 'No Data';
      
      const values = Object.values(bureauValues).filter(v => v !== null && v !== undefined && v !== '-');
      if (values.length === 0) return 'No Data';
      
      const uniqueValues = new Set(values);
      if (uniqueValues.size === 1) {
        return 'Match';
      } else {
        return 'Varies';
      }
    };

    return (
      <>
        {creditFactors.length > 0 ? (
          <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
            <Table>
              <TableHeader className="[&_tr]:border-b-0">
                {/* Header Row - Title and Description */}
                <TableRow className="border-b-0">
                  <TableHead colSpan={6} className="px-4 py-3">
                    <div className="space-y-1">
                      <h2 className="text-xl font-semibold text-gray-900">Bureau Comparison</h2>
                      <p className="text-sm text-gray-500 font-normal">Side-by-side comparison of key metrics across all three bureaus</p>
                    </div>
                  </TableHead>
                </TableRow>
                
                {/* Column Headers */}
                <TableRow className="border-b border-b-gray-200 border-t-0">
                  <TableHead className="pl-4 pr-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                    Metric
                  </TableHead>
                  <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }} />
                  <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                    Status
                  </TableHead>
                  <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                    TransUnion
                  </TableHead>
                  <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                    Experian
                  </TableHead>
                  <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                    Equifax
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {creditFactors.map((factor: CreditFactor, index: number) => {
                  const bureauValues = (factor as any).bureauValues || {};
                  
                // Helper function to format bureau values based on factor type
                const formatBureauValue = (value: any, factorTitle: string): string => {
                  if (value === null || value === undefined) return '-';
                  
                  const stringValue = String(value);
                  const titleLower = factorTitle.toLowerCase();
                  
                  // Payment History and Credit Utilization should be percentages
                  if (titleLower.includes('payment history') || titleLower.includes('credit utilization')) {
                    // If it's a number, add percentage sign
                    const numValue = Number(value);
                    if (!isNaN(numValue)) {
                      return `${numValue}%`;
                    }
                    // If already has %, return as is
                    if (stringValue.includes('%')) {
                      return stringValue;
                    }
                    // Otherwise add %
                    return `${value}%`;
                  }
                  
                  // Credit Age should be formatted as years and months
                  if (titleLower.includes('credit age') || titleLower.includes('age of credit')) {
                    // Try to parse as a number first
                    const numValue = Number(value);
                    
                    // If it's a valid number, format it from decimal years (e.g., 8.6 years)
                    if (!isNaN(numValue)) {
                      const years = Math.floor(numValue);
                      const months = Math.round((numValue - years) * 12);
                      
                      if (years === 0) {
                        return `${months} month${months !== 1 ? 's' : ''}`;
                      } else if (months === 0) {
                        return `${years} year${years !== 1 ? 's' : ''}`;
                      } else {
                        return `${years} year${years !== 1 ? 's' : ''} ${months} month${months !== 1 ? 's' : ''}`;
                      }
                    }
                    
                    // If not a number, return as is (might already be formatted)
                    return stringValue;
                  }
                  
                  // Default: return value as is
                  return stringValue;
                };

                const getInquiriesTotalValue = (values: Record<string, any>): string => {
                  const numbers = ['TransUnion', 'Experian', 'Equifax']
                    .map((bureau) => {
                      const raw = values[bureau];
                      if (raw === null || raw === undefined || raw === '-') return NaN;
                      const numeric = Number.parseFloat(String(raw).replace(/[^\d.-]/g, ''));
                      return Number.isFinite(numeric) ? numeric : NaN;
                    })
                    .filter((value) => Number.isFinite(value));

                  if (numbers.length === 0) return '-';
                  return String(numbers.reduce((sum, value) => sum + value, 0));
                };

                const metricValue =
                  factor.title.toLowerCase().includes('inquir')
                    ? getInquiriesTotalValue(bureauValues)
                    : factor.value;
                  
                  return (
                  <TableRow
                    key={factor.id}
                    className={`border-b border-gray-200 ${index === 0 ? 'border-t border-t-gray-200' : ''} last:border-b-0 hover:bg-gray-50/50 transition-colors`}
                  >
                    <TableCell className="pl-4 pr-2 py-3 font-medium text-gray-900">
                      {factor.title}
                    </TableCell>
                    <TableCell className="px-2 py-3 text-gray-900 font-semibold">
                      {metricValue}
                    </TableCell>
                    <TableCell className="px-2 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded border ${getStatusColorClass(bureauValues)}`}>
                        {getStatusLabel(bureauValues)}
                      </span>
                    </TableCell>
                    <TableCell className="px-2 py-3 text-gray-900">
                      {formatBureauValue(bureauValues.TransUnion, factor.title)}
                    </TableCell>
                    <TableCell className="px-2 py-3 text-gray-900">
                      {formatBureauValue(bureauValues.Experian, factor.title)}
                    </TableCell>
                    <TableCell className="px-2 py-3 text-gray-900">
                      {formatBureauValue(bureauValues.Equifax, factor.title)}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground text-lg">No credit factors available.</p>
          </div>
        )}
      </>
    );
  }

  function renderAccountsTab() {
    // Show error if there's an error
    if (error) {
      return (
        <div className="space-y-4">
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <p className="text-destructive">{error}</p>
                <Button onClick={fetchAnalytics} variant="outline">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Show skeleton when no data (keep showing even during refresh until we have data)
    if (!analytics) {
      const isLoadingData = isRefreshingFromIIQ || isLoading;
      return (
        <div className="space-y-4">
          <Card className={isLoadingData ? "animate-pulse" : ""}>
            <CardContent className="pt-6">
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center justify-between py-3 border-b last:border-0">
                    <div className="space-y-2 flex-1">
                      <div className="h-4 w-48 bg-muted rounded"></div>
                      <div className="h-3 w-32 bg-muted rounded"></div>
                    </div>
                    <div className="h-6 w-20 bg-muted rounded"></div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <div className="text-center py-8">
            <p className="text-muted-foreground">
              {isLoadingData
                ? 'Fetching your credit report...'
                : 'No account data available. Click "Get Latest Report" to fetch your credit information.'}
            </p>
          </div>
        </div>
      );
    }

    // Helper function to get payment status badge
  const getPaymentStatusBadge = (status: string) => {
    const normalizedStatus = (status || '').toUpperCase();

    if (status === 'C' || status === 'Current') {
      return (
        <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
          <Check className="h-3.5 w-3.5 text-white" />
        </div>
      );
    }
    if (normalizedStatus === 'CO' || normalizedStatus === 'RF' || normalizedStatus === 'BK') {
      return (
        <div className="w-6 h-6 rounded-full bg-black flex items-center justify-center">
          <span className="text-[10px] font-medium text-white">{normalizedStatus}</span>
        </div>
      );
    }
    if (status === '30' || status === '60' || status === '90') {
      const bgColor = status === '30' ? 'bg-orange-500' : status === '60' ? 'bg-orange-600' : 'bg-red-500';
      return (
        <div className={`w-6 h-6 rounded-full ${bgColor} flex items-center justify-center`}>
          <span className="text-xs font-medium text-white">{status}</span>
        </div>
      );
    }
    return (
      <div className="w-6 h-6 rounded-full bg-gray-200"></div>
    );
  };

  // Get payment summary for a bureau based on payment history
  const getBureauPaymentSummary = (accountId: string, bureau: 'TransUnion' | 'Experian' | 'Equifax'): string => {
    // Get all bureau variants for this account ID
    const bureauAccounts = accountsGroupedById.get(accountId) || [];
    
    // Find the account for this specific bureau
    const bureauAccount = bureauAccounts.find(acc => acc.bureau === bureau);
    
    // If no account for this bureau, return empty (no data)
    if (!bureauAccount) {
      return '';
    }
    
    // If no payment history, return empty
    if (!bureauAccount.paymentHistory || bureauAccount.paymentHistory.length === 0) {
      return '';
    }
    
    // Check payment history for late payments
    const latePayments = bureauAccount.paymentHistory
      .filter(p => p.status === '30' || p.status === '60' || p.status === '90')
      .sort((a, b) => {
        // Sort by severity: 90 > 60 > 30
        const order = { '90': 3, '60': 2, '30': 1 };
        return (order[b.status as '30' | '60' | '90'] || 0) - (order[a.status as '30' | '60' | '90'] || 0);
      });
    
    // If there are late payments, return the worst one
    if (latePayments.length > 0) {
      return latePayments[0].status;
    }
    
    // If all payments are current, return 'C'
    return 'C';
  };

  const formatMonthYear = (dateString: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    });
  };

  const formatPaymentResponsibility = (value?: string): string => {
    if (!value || !value.trim()) return '-';
    return value
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

    // Calculate summary statistics by grouping all bureau variants under account id
    const uniqueAccountGroups = Array.from(accountsGroupedById.values());
    const totalAccounts = uniqueAccountGroups.length;
    const negativeAccounts = uniqueAccountGroups.filter(group => group.some(account => account.negativeAccount === true)).length;
    const lateAccounts = uniqueAccountGroups.filter(group => {
      const hasNegative = group.some(account => account.negativeAccount === true);
      if (hasNegative) return false;
      return group.some(account => account.isLate === true);
    }).length;
    const inDisputeAccounts = uniqueAccountGroups.filter(group => group.some(account => account.accountStatus === 'In Dispute')).length;

    return (
      <>
            {accounts.length > 0 ? (
              <>
                {/* Summary Cards */}
                <div className="flex gap-4 mb-6 justify-center max-w-7xl mx-auto">
                  {/* Total Accounts Card */}
                  <div className="bg-white border border-gray-200 rounded-xl shadow-sm flex-1 max-w-[480px] h-[108px] p-4 relative">
                    <div className="flex items-center gap-2 mb-4">
                      <CreditCard className="h-5 w-5 text-gray-400" strokeWidth={1.5} />
                      <span className="text-sm font-medium text-gray-900">Total Accounts</span>
                    </div>
                    <div className="text-3xl font-semibold text-gray-900 tracking-tight">
                      {totalAccounts}
                    </div>
                    {/* Optional: Add trend indicator
                    <div className="absolute right-4 top-[45px] text-xs font-medium text-green-600">
                      +8
                    </div>
                    */}
                  </div>

                  {/* Negative Account Card */}
                  <div className={`bg-white border border-gray-200 ${negativeAccounts > 0 ? 'border-t-2 border-t-red-600' : ''} rounded-xl shadow-sm flex-1 max-w-[480px] h-[108px] p-4 relative`}>
                    <div className="flex items-center gap-2 mb-4">
                      <CircleX className={`h-5 w-5 ${negativeAccounts > 0 ? 'text-red-600' : 'text-gray-400'}`} strokeWidth={1.5} />
                      <span className="text-sm font-medium text-gray-900">Negative Account</span>
                    </div>
                    <div className="text-3xl font-semibold text-gray-900 tracking-tight">
                      {negativeAccounts}
                    </div>
                    {/* Optional: Add trend indicator
                    <div className="absolute right-4 top-[45px] text-xs font-medium text-green-600">
                      +8
                    </div>
                    */}
                  </div>

                  {/* Lates Card */}
                  <div className={`bg-white border border-gray-200 ${lateAccounts > 0 ? 'border-t-2 border-t-yellow-500' : ''} rounded-xl shadow-sm flex-1 max-w-[480px] h-[108px] p-4 relative`}>
                    <div className="flex items-center gap-2 mb-4">
                      <AlertTriangle className={`h-5 w-5 ${lateAccounts > 0 ? 'text-yellow-600' : 'text-gray-400'}`} strokeWidth={1.5} />
                      <span className="text-sm font-medium text-gray-900">Lates</span>
                    </div>
                    <div className="text-3xl font-semibold text-gray-900 tracking-tight">
                      {lateAccounts}
                    </div>
                  </div>

                  {/* In Dispute Card */}
                  <div className="bg-white border border-gray-200 rounded-xl shadow-sm flex-1 max-w-[480px] h-[108px] p-4 relative">
                    <div className="flex items-center gap-2 mb-4">
                      <Clock className="h-5 w-5 text-gray-400" strokeWidth={1.5} />
                      <span className="text-sm font-medium text-gray-900">In Dispute</span>
                    </div>
                    <div className="text-3xl font-semibold text-gray-900 tracking-tight">
                      {inDisputeAccounts}
                    </div>
                    {/* Optional: Add trend indicator
                    <div className="absolute right-4 top-[45px] text-xs font-medium text-green-600">
                      +8
                    </div>
                    */}
                  </div>
                </div>

                {/* Accounts Table */}
                <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                  <Table>
                  <TableHeader className="[&_tr]:border-b-0">
                    {/* First Header Row - Title and Filter */}
                    <TableRow className="border-b-0">
                      <TableHead colSpan={12} className="px-4 py-3">
                        <div className="flex items-center justify-between">
                          <h2 className="text-xl font-semibold text-gray-900">Accounts</h2>
                          <Select value={accountStatusFilter} onValueChange={setAccountStatusFilter}>
                            <SelectTrigger className="min-w-[180px] w-auto h-10 rounded-lg focus:ring-0 focus:ring-offset-0">
                              <span className="text-sm whitespace-nowrap">
                                <span className="text-gray-500">Account Status: </span>
                                <span className="font-semibold text-black">
                                  {accountStatusFilter === 'all' ? 'All' :
                                   accountStatusFilter === 'needs-work' ? 'Needs Work' :
                                   accountStatusFilter === 'healthy' ? 'Healthy' : 'All'}
                                </span>
                              </span>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All</SelectItem>
                              <SelectItem value="needs-work">Needs Work</SelectItem>
                              <SelectItem value="healthy">Healthy</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </TableHead>
                    </TableRow>
                    
                    {/* Second Header Row - Column Headers */}
                    <TableRow className="border-b border-b-gray-200 border-t-0">
                      <TableHead className="pl-4 pr-2 py-2 text-sm font-medium cursor-pointer" style={{ color: '#737373', fontWeight: 500 }} onClick={() => handleAccountSort('creditorName')}>
                        <div className="flex items-center gap-2">
                          Creditor
                          {accountSort.field === 'creditorName' ? (
                            accountSort.direction === 'asc' ? <ArrowUp className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} /> : <ArrowDown className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} />
                          ) : (
                            <ArrowUpDown className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} />
                          )}
                        </div>
                      </TableHead>
                      <TableHead className="px-2 py-2 text-sm font-medium cursor-pointer" style={{ color: '#737373', fontWeight: 500 }} onClick={() => handleAccountSort('accountType')}>
                        <div className="flex items-center gap-2">
                          Type
                          {accountSort.field === 'accountType' ? (
                            accountSort.direction === 'asc' ? <ArrowUp className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} /> : <ArrowDown className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} />
                          ) : (
                            <ArrowUpDown className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} />
                          )}
                        </div>
                      </TableHead>
                      <TableHead className="px-2 py-2 text-sm font-medium cursor-pointer" style={{ color: '#737373', fontWeight: 500 }} onClick={() => handleAccountSort('accountStatus')}>
                        <div className="flex items-center gap-2">
                          Status
                          {accountSort.field === 'accountStatus' ? (
                            accountSort.direction === 'asc' ? <ArrowUp className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} /> : <ArrowDown className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} />
                          ) : (
                            <ArrowUpDown className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} />
                          )}
                        </div>
                      </TableHead>
                      <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                        Payment Status
                      </TableHead>
                      <TableHead className="px-2 py-2 text-sm font-medium cursor-pointer" style={{ color: '#737373', fontWeight: 500 }} onClick={() => handleAccountSort('currentBalance')}>
                        <div className="flex items-center gap-2">
                          Balance
                          {accountSort.field === 'currentBalance' ? (
                            accountSort.direction === 'asc' ? <ArrowUp className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} /> : <ArrowDown className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} />
                          ) : (
                            <ArrowUpDown className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} />
                          )}
                        </div>
                      </TableHead>
                      <TableHead className="px-2 py-2 text-sm font-medium cursor-pointer" style={{ color: '#737373', fontWeight: 500 }} onClick={() => handleAccountSort('creditLimit')}>
                        <div className="flex items-center gap-2">
                          Credit Limit
                          {accountSort.field === 'creditLimit' ? (
                            accountSort.direction === 'asc' ? <ArrowUp className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} /> : <ArrowDown className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} />
                          ) : (
                            <ArrowUpDown className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} />
                          )}
                        </div>
                      </TableHead>
                      <TableHead className="px-2 py-2 text-sm font-medium cursor-pointer" style={{ color: '#737373', fontWeight: 500 }}>
                        <div className="flex items-center gap-2">
                          Util
                          <ArrowUpDown className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} />
                        </div>
                      </TableHead>
                      <TableHead className="px-2 py-2 text-sm font-medium cursor-pointer" style={{ color: '#737373', fontWeight: 500 }} onClick={() => handleAccountSort('dateOpened')}>
                        <div className="flex items-center gap-2">
                          Date Opened
                          {accountSort.field === 'dateOpened' ? (
                            accountSort.direction === 'asc' ? <ArrowUp className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} /> : <ArrowDown className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} />
                          ) : (
                            <ArrowUpDown className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} />
                          )}
                        </div>
                      </TableHead>
                      <TableHead className="w-[60px] px-2 py-2 text-sm font-medium text-center" style={{ color: '#737373', fontWeight: 500 }}>TU</TableHead>
                      <TableHead className="w-[60px] px-2 py-2 text-sm font-medium text-center" style={{ color: '#737373', fontWeight: 500 }}>EX</TableHead>
                      <TableHead className="w-[60px] px-2 py-2 text-sm font-medium text-center" style={{ color: '#737373', fontWeight: 500 }}>EQ</TableHead>
                      <TableHead className="w-[100px] px-2 py-2 text-sm font-medium text-right" style={{ color: '#737373', fontWeight: 500 }}>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody className="[&_tr:first-child]:border-t [&_tr:first-child]:border-t-gray-200">
                    {sortedAccounts.map((account) => {
                      const accountVariants = accountsGroupedById.get(account.id) || [];
                      const hasNegativeInAnyBureau = accountVariants.some(variant => variant.negativeAccount === true);
                      const hasLateInAnyBureau = accountVariants.some(variant => variant.isLate === true);
                      const rowBorderClass = hasNegativeInAnyBureau
                        ? 'border-l-2 border-l-red-600'
                        : hasLateInAnyBureau
                          ? 'border-l-2 border-l-yellow-500'
                          : '';

                      // Calculate utilization
                      let utilizationDisplay = '-';
                      if (account.creditLimit && account.currentBalance) {
                        // Remove $, commas, and any other non-numeric characters except decimal point and minus
                        const balance = parseFloat(account.currentBalance.replace(/[^0-9.-]/g, ''));
                        const limit = parseFloat(account.creditLimit.replace(/[^0-9.-]/g, ''));
                        
                        // If both are 0, show "-", otherwise calculate percentage
                        if (balance === 0 && limit === 0) {
                          utilizationDisplay = '-';
                        } else if (limit > 0 && !isNaN(balance) && !isNaN(limit)) {
                          utilizationDisplay = `${Math.round((balance / limit) * 100)}%`;
                        }
                      }
                      
                      const isExpanded = expandedAccountId === account.id;
                      
                      return (
                        <React.Fragment key={account.id}>
                        <TableRow
                          className={`border-b border-gray-200 cursor-pointer hover:bg-gray-50 ${rowBorderClass}`}
                          onClick={() => setExpandedAccountId(isExpanded ? null : account.id)}
                        >
                          <TableCell className="pl-4 pr-2 py-3">
                            <div className="flex flex-col">
                              <span className="text-sm font-normal text-gray-900">{account.creditorDisplayName || account.creditorName}</span>
                              {account.accountNumber && (
                                <span className="text-xs text-gray-400 mt-0.5">{account.accountNumber}</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="px-2 py-3">
                            <div className="flex flex-col">
                              <span className="text-sm font-normal text-gray-900 leading-tight">{account.accountType}</span>
                              {hasNegativeInAnyBureau && (
                                <span className="text-[10px] font-medium text-red-600 leading-tight">Negative</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="px-2 py-3">
                            {account.accountStatus === 'Negative' ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-lg border border-red-300 bg-red-50 text-xs font-medium text-red-700">
                                Negative
                              </span>
                            ) : account.accountStatus === 'In Dispute' ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-lg border border-orange-300 bg-orange-50 text-xs font-medium text-orange-700">
                                In Dispute
                              </span>
                            ) : account.accountStatus === 'Paid' ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-lg border border-green-300 bg-white text-xs font-medium text-gray-900">
                                Paid
                              </span>
                            ) : account.accountStatus === 'Open' ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-lg border border-gray-300 bg-white text-xs font-medium text-gray-900">
                                Open
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-lg border border-gray-300 bg-white text-xs font-medium text-gray-900">
                                {account.accountStatus}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="px-2 py-3 text-sm font-normal text-gray-900">{account.paymentStatus || '-'}</TableCell>
                          <TableCell className="px-2 py-3 text-sm font-normal text-gray-900">{account.currentBalance}</TableCell>
                          <TableCell className="px-2 py-3 text-sm font-normal text-gray-900">{account.creditLimit}</TableCell>
                          <TableCell className="px-2 py-3 text-sm font-normal text-gray-900">{utilizationDisplay}</TableCell>
                          <TableCell className="px-2 py-3 text-sm font-normal text-gray-900">{formatMonthYear(account.dateOpened)}</TableCell>
                          <TableCell className="w-[60px] px-2 py-3">
                            <div className="flex items-center justify-center">
                              {(() => {
                                const status = getBureauPaymentSummary(account.id, 'TransUnion');
                                return status ? getPaymentStatusBadge(status) : <div className="w-6 h-6 rounded-full bg-gray-200"></div>;
                              })()}
                            </div>
                          </TableCell>
                          <TableCell className="w-[60px] px-2 py-3">
                            <div className="flex items-center justify-center">
                              {(() => {
                                const status = getBureauPaymentSummary(account.id, 'Experian');
                                return status ? getPaymentStatusBadge(status) : <div className="w-6 h-6 rounded-full bg-gray-200"></div>;
                              })()}
                            </div>
                          </TableCell>
                          <TableCell className="w-[60px] px-2 py-3">
                            <div className="flex items-center justify-center">
                              {(() => {
                                const status = getBureauPaymentSummary(account.id, 'Equifax');
                                return status ? getPaymentStatusBadge(status) : <div className="w-6 h-6 rounded-full bg-gray-200"></div>;
                              })()}
                            </div>
                          </TableCell>
                          <TableCell className="w-[100px] px-2 py-3 text-right">
                            <div className="flex justify-end">
                              <Button variant="outline" size="sm" className="h-8 px-3 text-sm">
                                Dispute
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        
                        {/* Expanded Details Row */}
                        {isExpanded && (() => {
                          // Get all bureau variants for this account ID
                          const bureauAccounts = accountsGroupedById.get(account.id) || [];
                          const tuAccount = bureauAccounts.find(acc => acc.bureau === 'TransUnion');
                          const exAccount = bureauAccounts.find(acc => acc.bureau === 'Experian');
                          const eqAccount = bureauAccounts.find(acc => acc.bureau === 'Equifax');
                          
                          // Helper to get field value for each bureau
                          const getBureauData = (fieldGetter: (acc: AccountInformation) => string) => ({
                            tu: tuAccount ? fieldGetter(tuAccount) : '-',
                            ex: exAccount ? fieldGetter(exAccount) : '-',
                            eq: eqAccount ? fieldGetter(eqAccount) : '-'
                          });
                          
                          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                          const monthNameToIndex: Record<string, number> = {
                            jan: 1,
                            january: 1,
                            feb: 2,
                            february: 2,
                            mar: 3,
                            march: 3,
                            apr: 4,
                            april: 4,
                            may: 5,
                            jun: 6,
                            june: 6,
                            jul: 7,
                            july: 7,
                            aug: 8,
                            august: 8,
                            sep: 9,
                            sept: 9,
                            september: 9,
                            oct: 10,
                            october: 10,
                            nov: 11,
                            november: 11,
                            dec: 12,
                            december: 12,
                          };

                          const parsePaymentYearMonth = (rawDate: string): { year: number; month: number } | null => {
                            if (!rawDate) return null;
                            const value = rawDate.trim();

                            const isoMatch = /^(\d{4})-(\d{2})$/.exec(value);
                            if (isoMatch) {
                              const year = Number(isoMatch[1]);
                              const month = Number(isoMatch[2]);
                              if (year && month >= 1 && month <= 12) {
                                return { year, month };
                              }
                            }

                            const monthNameMatch = /^([A-Za-z]+)\s+(\d{4})$/.exec(value);
                            if (monthNameMatch) {
                              const month = monthNameToIndex[monthNameMatch[1].toLowerCase()];
                              const year = Number(monthNameMatch[2]);
                              if (month && year) {
                                return { year, month };
                              }
                            }

                            return null;
                          };

                          const toMonthIndex = (year: number, month: number) => year * 12 + (month - 1);
                          const toYearMonthKey = (year: number, month: number) => `${year}-${String(month).padStart(2, '0')}`;

                          const parsedHistoryMonthIndexes = bureauAccounts
                            .flatMap((acc) => acc.paymentHistory || [])
                            .map((payment) => parsePaymentYearMonth(payment.date))
                            .filter((date): date is { year: number; month: number } => date !== null)
                            .map((date) => toMonthIndex(date.year, date.month));

                          const latestHistoryMonthIndex = parsedHistoryMonthIndexes.length > 0
                            ? Math.max(...parsedHistoryMonthIndexes)
                            : null;
                          const earliestHistoryMonthIndex = parsedHistoryMonthIndexes.length > 0
                            ? Math.min(...parsedHistoryMonthIndexes)
                            : null;

                          const currentMonthIndex = (() => {
                            const now = new Date();
                            return now.getFullYear() * 12 + now.getMonth();
                          })();

                          const anchorMonthIndex = latestHistoryMonthIndex ?? currentMonthIndex;
                          const defaultStartMonthIndex = anchorMonthIndex - 23;
                          const startMonthIndex =
                            earliestHistoryMonthIndex !== null
                              ? Math.min(defaultStartMonthIndex, earliestHistoryMonthIndex)
                              : defaultStartMonthIndex;
                          const totalMonths = Math.max(24, anchorMonthIndex - startMonthIndex + 1);

                          const paymentHistoryColumns = Array.from({ length: totalMonths }, (_, idx) => {
                            const monthIndex = anchorMonthIndex - idx;
                            const year = Math.floor(monthIndex / 12);
                            const month = (monthIndex % 12) + 1;
                            return {
                              key: toYearMonthKey(year, month),
                              monthLabel: monthNames[month - 1],
                              yearLabel: String(year).slice(-2),
                            };
                          });

                          // Create 24-month payment history array for each bureau
                          const createPaymentHistoryGrid = (bureauAccount: AccountInformation | undefined) => {
                            if (!bureauAccount || !bureauAccount.paymentHistory) {
                              return paymentHistoryColumns.map(() => null);
                            }

                            // Map payment history by normalized YYYY-MM date key
                            const paymentMap = new Map<string, string>();
                            bureauAccount.paymentHistory.forEach((payment) => {
                              const parsedDate = parsePaymentYearMonth(payment.date);
                              if (!parsedDate) return;
                              paymentMap.set(toYearMonthKey(parsedDate.year, parsedDate.month), payment.status);
                            });

                            return paymentHistoryColumns.map((column) => paymentMap.get(column.key) || null);
                          };
                          
                          const tuPaymentStatuses = createPaymentHistoryGrid(tuAccount);
                          const exPaymentStatuses = createPaymentHistoryGrid(exAccount);
                          const eqPaymentStatuses = createPaymentHistoryGrid(eqAccount);
                          
                          return (
                            <TableRow className="bg-gray-50">
                              <TableCell colSpan={12} className="p-0">
                                <div className="w-0 min-w-full overflow-x-hidden p-6">
                                {/* Account Details Section */}
                                <div className="mb-6 bg-white border border-gray-200 rounded-lg overflow-hidden">
                                  <Table className="table-fixed w-full">
                                    <TableHeader className="[&_tr]:border-b">
                                      <TableRow>
                                        <TableHead className="w-[22%] px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                                          {account.creditorDisplayName || account.creditorName || 'Account'}
                                        </TableHead>
                                        <TableHead className="w-[26%] px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>TransUnion</TableHead>
                                        <TableHead className="w-[26%] px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>Experian</TableHead>
                                        <TableHead className="w-[26%] px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>Equifax</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      <TableRow className="border-b border-gray-200">
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">Account #</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.accountNumber || '-').tu}</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.accountNumber || '-').ex}</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.accountNumber || '-').eq}</TableCell>
                                      </TableRow>
                                      <TableRow className="border-b border-gray-200">
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">Account Type</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.accountType).tu}</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.accountType).ex}</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.accountType).eq}</TableCell>
                                      </TableRow>
                                      <TableRow className="border-b border-gray-200">
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">Account Type Details</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.accountTypeDetailed || '-').tu}</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.accountTypeDetailed || '-').ex}</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.accountTypeDetailed || '-').eq}</TableCell>
                                      </TableRow>
                                      <TableRow className="border-b border-gray-200">
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">Account Status</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.accountStatus).tu}</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.accountStatus).ex}</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.accountStatus).eq}</TableCell>
                                      </TableRow>
                                      <TableRow className="border-b border-gray-200">
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">Payment Status</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.paymentStatus || '-').tu}</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.paymentStatus || '-').ex}</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.paymentStatus || '-').eq}</TableCell>
                                      </TableRow>
                                      <TableRow className="border-b border-gray-200">
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">Payment Responsibility</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => formatPaymentResponsibility(acc.paymentResponsibility)).tu}</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => formatPaymentResponsibility(acc.paymentResponsibility)).ex}</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => formatPaymentResponsibility(acc.paymentResponsibility)).eq}</TableCell>
                                      </TableRow>
                                      <TableRow className="border-b border-gray-200">
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">Monthly Payment</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.monthlyPayment || '$0').tu}</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.monthlyPayment || '$0').ex}</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.monthlyPayment || '$0').eq}</TableCell>
                                      </TableRow>
                                      <TableRow className="border-b border-gray-200">
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">Date Opened</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => formatMonthYear(acc.dateOpened)).tu}</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => formatMonthYear(acc.dateOpened)).ex}</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => formatMonthYear(acc.dateOpened)).eq}</TableCell>
                                      </TableRow>
                                      <TableRow className="border-b border-gray-200">
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">Balance</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.currentBalance).tu}</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.currentBalance).ex}</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.currentBalance).eq}</TableCell>
                                      </TableRow>
                                      <TableRow className="border-b border-gray-200">
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">No. of Months (terms)</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.termLength || '0').tu}</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.termLength || '0').ex}</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.termLength || '0').eq}</TableCell>
                                      </TableRow>
                                      <TableRow className="border-b border-gray-200">
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">High Credit</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.highBalance || '$0').tu}</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.highBalance || '$0').ex}</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.highBalance || '$0').eq}</TableCell>
                                      </TableRow>
                                      <TableRow className="border-b border-gray-200">
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">Credit Limit</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.creditLimit).tu}</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.creditLimit).ex}</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.creditLimit).eq}</TableCell>
                                      </TableRow>
                                      <TableRow className="border-b border-gray-200">
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">Past Due</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.amountPastDue || '$0').tu}</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.amountPastDue || '$0').ex}</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.amountPastDue || '$0').eq}</TableCell>
                                      </TableRow>
                                      <TableRow className="border-b-0">
                                        <TableCell className="px-2 py-2 text-sm text-gray-900 align-top">Comments</TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900 align-top">
                                          {tuAccount?.comments && tuAccount.comments.length > 0 ? (
                                            <ul className="list-disc pl-5 space-y-1">
                                              {tuAccount.comments.map((comment, idx) => (
                                                <li key={`tu-comment-${idx}`}>{comment}</li>
                                              ))}
                                            </ul>
                                          ) : (
                                            '-'
                                          )}
                                        </TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900 align-top">
                                          {exAccount?.comments && exAccount.comments.length > 0 ? (
                                            <ul className="list-disc pl-5 space-y-1">
                                              {exAccount.comments.map((comment, idx) => (
                                                <li key={`ex-comment-${idx}`}>{comment}</li>
                                              ))}
                                            </ul>
                                          ) : (
                                            '-'
                                          )}
                                        </TableCell>
                                        <TableCell className="px-2 py-2 text-sm text-gray-900 align-top">
                                          {eqAccount?.comments && eqAccount.comments.length > 0 ? (
                                            <ul className="list-disc pl-5 space-y-1">
                                              {eqAccount.comments.map((comment, idx) => (
                                                <li key={`eq-comment-${idx}`}>{comment}</li>
                                              ))}
                                            </ul>
                                          ) : (
                                            '-'
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    </TableBody>
                                  </Table>
                                </div>

                              {/* Two-Year Payment History Section */}
                              <div className="mb-6 w-full max-w-full overflow-hidden bg-white border border-gray-200 rounded-lg p-4">
                                <div className="mb-4">
                                  <h3 className="text-lg font-semibold text-gray-900">Payment History</h3>
                                </div>
                                
                                <div className="block w-full max-w-full overflow-x-auto">
                                  {/* Payment History Grid */}
                                  <div
                                    className="min-w-[1120px]"
                                    style={{ minWidth: `${114 + paymentHistoryColumns.length * 41}px` }}
                                  >
                                    {/* Month Row */}
                                    <div className="flex border-b border-gray-200">
                                      <div className="sticky left-0 z-20 w-[114px] shrink-0 border-r border-gray-200 bg-white px-2 py-2 text-xs text-gray-600 font-medium">
                                        Month
                                      </div>
                                      {paymentHistoryColumns.map((column, idx) => (
                                        <div key={`month-${column.key}-${idx}`} className="w-[41px] px-2 py-2 text-xs text-gray-600 text-center">{column.monthLabel}</div>
                                      ))}
                                    </div>
                                    
                                    {/* Year Row */}
                                    <div className="flex border-b border-gray-200">
                                      <div className="sticky left-0 z-20 w-[114px] shrink-0 border-r border-gray-200 bg-white px-2 py-2 text-xs text-gray-600 font-medium">
                                        Year
                                      </div>
                                      {paymentHistoryColumns.map((column, idx) => (
                                        <div key={`year-${column.key}-${idx}`} className="w-[41px] px-2 py-2 text-xs text-gray-600 text-center">{column.yearLabel}</div>
                                      ))}
                                    </div>
                                    
                                    {/* TransUnion Row */}
                                    <div className="flex border-b border-gray-200">
                                      <div className="sticky left-0 z-10 w-[114px] shrink-0 border-r border-gray-200 bg-white px-2 py-2 text-sm text-gray-900">
                                        TransUnion
                                      </div>
                                      {tuPaymentStatuses.map((status, idx) => (
                                        <div key={idx} className="w-[41px] px-2 py-2 flex items-center justify-center">
                                          {status ? getPaymentStatusBadge(status) : (
                                            <div className="w-6 h-6 rounded-full bg-gray-200"></div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                    
                                    {/* Experian Row */}
                                    <div className="flex border-b border-gray-200">
                                      <div className="sticky left-0 z-10 w-[114px] shrink-0 border-r border-gray-200 bg-white px-2 py-2 text-sm text-gray-900">
                                        Experian
                                      </div>
                                      {exPaymentStatuses.map((status, idx) => (
                                        <div key={idx} className="w-[41px] px-2 py-2 flex items-center justify-center">
                                          {status ? getPaymentStatusBadge(status) : (
                                            <div className="w-6 h-6 rounded-full bg-gray-200"></div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                    
                                    {/* Equifax Row */}
                                    <div className="flex">
                                      <div className="sticky left-0 z-10 w-[114px] shrink-0 border-r border-gray-200 bg-white px-2 py-2 text-sm text-gray-900">
                                        Equifax
                                      </div>
                                      {eqPaymentStatuses.map((status, idx) => (
                                        <div key={idx} className="w-[41px] px-2 py-2 flex items-center justify-center">
                                          {status ? getPaymentStatusBadge(status) : (
                                            <div className="w-6 h-6 rounded-full bg-gray-200"></div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Bank Info Section */}
                              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                <Table className="table-fixed w-full">
                                  <TableHeader className="[&_tr]:border-b">
                                    <TableRow>
                                      <TableHead className="w-[22%] px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>Bank Info</TableHead>
                                      <TableHead className="w-[26%] px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>TransUnion</TableHead>
                                      <TableHead className="w-[26%] px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>Experian</TableHead>
                                      <TableHead className="w-[26%] px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>Equifax</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    <TableRow className="border-b border-gray-200">
                                      <TableCell className="px-2 py-2 text-sm text-gray-900">Name</TableCell>
                                      <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.creditorDisplayName || acc.creditorName).tu}</TableCell>
                                      <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.creditorDisplayName || acc.creditorName).ex}</TableCell>
                                      <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.creditorDisplayName || acc.creditorName).eq}</TableCell>
                                    </TableRow>
                                    <TableRow className="border-b border-gray-200">
                                      <TableCell className="px-2 py-2 text-sm text-gray-900">Phone</TableCell>
                                      <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.phone || '-').tu}</TableCell>
                                      <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.phone || '-').ex}</TableCell>
                                      <TableCell className="px-2 py-2 text-sm text-gray-900">{getBureauData(acc => acc.phone || '-').eq}</TableCell>
                                    </TableRow>
                                    <TableRow className="border-b-0">
                                      <TableCell className="px-2 py-2 text-sm text-gray-900 align-top">Address</TableCell>
                                      <TableCell className="px-2 py-2 text-sm text-gray-900 align-top">
                                        <div className="flex flex-col items-start">
                                          {getBureauData(acc => acc.address ? acc.address.street : '-').tu !== '-' ? (
                                            <>
                                              <span>{getBureauData(acc => acc.address ? acc.address.street : '-').tu}</span>
                                              <span>{getBureauData(acc => acc.address ? `${acc.address.city}, ${acc.address.state} ${acc.address.postal}` : '-').tu}</span>
                                            </>
                                          ) : '-'}
                                        </div>
                                      </TableCell>
                                      <TableCell className="px-2 py-2 text-sm text-gray-900 align-top">
                                        <div className="flex flex-col items-start">
                                          {getBureauData(acc => acc.address ? acc.address.street : '-').ex !== '-' ? (
                                            <>
                                              <span>{getBureauData(acc => acc.address ? acc.address.street : '-').ex}</span>
                                              <span>{getBureauData(acc => acc.address ? `${acc.address.city}, ${acc.address.state} ${acc.address.postal}` : '-').ex}</span>
                                            </>
                                          ) : '-'}
                                        </div>
                                      </TableCell>
                                      <TableCell className="px-2 py-2 text-sm text-gray-900 align-top">
                                        <div className="flex flex-col items-start">
                                          {getBureauData(acc => acc.address ? acc.address.street : '-').eq !== '-' ? (
                                            <>
                                              <span>{getBureauData(acc => acc.address ? acc.address.street : '-').eq}</span>
                                              <span>{getBureauData(acc => acc.address ? `${acc.address.city}, ${acc.address.state} ${acc.address.postal}` : '-').eq}</span>
                                            </>
                                          ) : '-'}
                              </div>
                                      </TableCell>
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              </div>
                                </div>
                            </TableCell>
                          </TableRow>
                          );
                        })()}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">No credit accounts found.</p>
            )}
      </>
    );
  }

  function renderInquiriesTab() {
    // Show error if there's an error
    if (error) {
      return (
        <div className="space-y-4">
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <p className="text-destructive">{error}</p>
                <Button onClick={fetchAnalytics} variant="outline">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Show skeleton when no data (keep showing even during refresh until we have data)
    if (!analytics) {
      const isLoadingData = isRefreshingFromIIQ || isLoading;
      return (
        <div className="space-y-4">
          <Card className={isLoadingData ? "animate-pulse" : ""}>
            <CardContent className="pt-6">
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center justify-between py-3 border-b last:border-0">
                    <div className="space-y-2 flex-1">
                      <div className="h-4 w-40 bg-muted rounded"></div>
                      <div className="h-3 w-24 bg-muted rounded"></div>
                    </div>
                    <div className="h-6 w-16 bg-muted rounded"></div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <div className="text-center py-8">
            <p className="text-muted-foreground">
              {isLoadingData
                ? 'Fetching your credit report...'
                : 'No inquiry data available. Click "Get Latest Report" to fetch your credit information.'}
            </p>
          </div>
        </div>
      );
    }

    return (
      <>
            {inquiries.length > 0 ? (
              <>

              {/* Inquiries Table */}
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                <Table>
                  <TableHeader className="[&_tr]:border-b-0">
                    {/* First Header Row - Title and Filter */}
                    <TableRow className="border-b-0">
                      <TableHead colSpan={4} className="px-4 py-3">
                        <div className="flex items-center justify-between">
                          <h2 className="text-xl font-semibold text-gray-900">Credit Inquiries <span className="text-gray-500 font-normal">({inquiries.length})</span></h2>
                          <Select value={inquiryBureauFilter} onValueChange={setInquiryBureauFilter}>
                            <SelectTrigger className="min-w-[140px] w-auto h-10 rounded-lg focus:ring-0 focus:ring-offset-0">
                              <span className="text-sm whitespace-nowrap">
                                <span className="text-gray-500">Bureaus: </span>
                                <span className="font-semibold text-black">
                                  {inquiryBureauFilter === 'all' ? 'All' :
                                   inquiryBureauFilter === 'transunion' ? 'TransUnion' :
                                   inquiryBureauFilter === 'experian' ? 'Experian' :
                                   inquiryBureauFilter === 'equifax' ? 'Equifax' : 'All'}
                                </span>
                              </span>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All</SelectItem>
                              <SelectItem value="transunion">TransUnion</SelectItem>
                              <SelectItem value="experian">Experian</SelectItem>
                              <SelectItem value="equifax">Equifax</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </TableHead>
                    </TableRow>
                    
                    {/* Second Header Row - Column Headers */}
                    <TableRow className="border-b border-b-gray-200 border-t-0">
                      <TableHead className="pl-4 pr-2 py-2 text-sm font-medium cursor-pointer" style={{ color: '#737373', fontWeight: 500 }} onClick={() => handleInquirySort('company')}>
                        <div className="flex items-center gap-2">
                          Creditor
                          {inquirySort.field === 'company' ? (
                            inquirySort.direction === 'asc' ? <ArrowUp className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} /> : <ArrowDown className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} />
                          ) : (
                            <ArrowUpDown className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} />
                          )}
                        </div>
                      </TableHead>
                      <TableHead className="px-2 py-2 text-sm font-medium cursor-pointer" style={{ color: '#737373', fontWeight: 500 }} onClick={() => handleInquirySort('date')}>
                        <div className="flex items-center gap-2">
                          Date
                          {inquirySort.field === 'date' ? (
                            inquirySort.direction === 'asc' ? <ArrowUp className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} /> : <ArrowDown className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} />
                          ) : (
                            <ArrowUpDown className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} />
                          )}
                        </div>
                      </TableHead>
                      <TableHead className="px-2 py-2 text-sm font-medium cursor-pointer text-center" style={{ color: '#737373', fontWeight: 500 }} onClick={() => handleInquirySort('bureau')}>
                        <div className="flex items-center justify-center gap-2">
                          Bureau
                          {inquirySort.field === 'bureau' ? (
                            inquirySort.direction === 'asc' ? <ArrowUp className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} /> : <ArrowDown className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} />
                          ) : (
                            <ArrowUpDown className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} />
                          )}
                        </div>
                      </TableHead>
                      <TableHead className="px-2 py-2 text-sm font-medium text-center" style={{ color: '#737373', fontWeight: 500 }}>
                        Expires in
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedInquiries.map((inquiry) => {
                      // Map bureau names to abbreviations
                      const getBureauAbbr = (bureau: string): string => {
                        const lower = bureau.toLowerCase();
                        if (lower.includes('transunion')) return 'TU';
                        if (lower.includes('experian')) return 'EX';
                        if (lower.includes('equifax')) return 'EQ';
                        return bureau.substring(0, 2).toUpperCase();
                      };

                      // Calculate expiration (24 months from inquiry date)
                      const calculateExpiration = (dateString: string): { text: string; isExpired: boolean } => {
                        try {
                          const inquiryDate = new Date(dateString);
                          const expirationDate = new Date(inquiryDate);
                          expirationDate.setMonth(expirationDate.getMonth() + 24);
                          
                          const now = new Date();
                          
                          if (now >= expirationDate) {
                            return { text: 'Expired', isExpired: true };
                          }
                          
                          // Calculate months remaining
                          const monthsRemaining = Math.floor((expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24 * 30.44));
                          
                          if (monthsRemaining === 0) {
                            const daysRemaining = Math.floor((expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                            return { text: `${daysRemaining}d`, isExpired: false };
                          } else if (monthsRemaining < 12) {
                            return { text: `${monthsRemaining}m`, isExpired: false };
                          } else {
                            const years = Math.floor(monthsRemaining / 12);
                            const months = monthsRemaining % 12;
                            if (months === 0) {
                              return { text: `${years}y`, isExpired: false };
                            } else {
                              return { text: `${years}y ${months}m`, isExpired: false };
                            }
                          }
                        } catch (error) {
                          return { text: '-', isExpired: false };
                        }
                      };

                      const expiration = calculateExpiration(inquiry.date);

                      return (
                      <TableRow key={inquiry.id} className="border-b border-gray-200 first:border-t first:border-t-gray-200 last:border-b-0 hover:bg-gray-50/50 transition-colors">
                        <TableCell className="pl-4 pr-2 py-3 font-medium text-gray-900">{inquiry.company}</TableCell>
                        <TableCell className="px-2 py-3 text-gray-900">{formatDate(inquiry.date)}</TableCell>
                        <TableCell className="px-2 py-3">
                          <div className="flex flex-col items-center justify-center gap-0.5 py-1">
                            <div className="w-10 h-6 flex items-center justify-center border border-gray-200 rounded bg-white">
                              <span className="text-xs font-semibold text-gray-700">{getBureauAbbr(inquiry.bureau)}</span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="px-2 py-3">
                          {expiration.isExpired ? (
                            <div className="flex flex-col items-center justify-center gap-0.5 py-1">
                              <div className="min-w-[60px] h-6 px-2 flex items-center justify-center border border-gray-200 rounded bg-white">
                                <span className="text-xs font-semibold text-red-600">
                                  {expiration.text}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className="text-center text-sm text-gray-900">
                              {expiration.text}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">No credit inquiries found.</p>
            )}
      </>
    );
  }

  function renderPersonalInfoTab() {
    // Show error if there's an error
    if (error) {
      return (
        <div className="space-y-4">
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <p className="text-destructive">{error}</p>
                <Button onClick={fetchAnalytics} variant="outline">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Show skeleton when no data (keep showing even during refresh until we have data)
    if (!analytics) {
      const isLoadingData = isRefreshingFromIIQ || isLoading;
      return (
        <div className="space-y-4">
          <Card className={isLoadingData ? "animate-pulse" : ""}>
            <CardHeader>
              <div className="h-5 w-32 bg-muted rounded"></div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-center justify-between py-2">
                    <div className="h-4 w-24 bg-muted rounded"></div>
                    <div className="h-4 w-48 bg-muted rounded"></div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <div className="text-center py-8">
            <p className="text-muted-foreground">
              {isLoadingData
                ? 'Fetching your credit report...'
                : 'No personal information available. Click "Get Latest Report" to fetch your credit information.'}
            </p>
          </div>
        </div>
      );
    }

    // Group personal info by bureau
    const personalInfoByBureau: { [key: string]: PersonalInformation } = {};
    personalInfo.forEach(info => {
      const bureauName = info.bureau.toLowerCase();
      if (bureauName.includes('transunion') || bureauName.includes('tu')) {
        personalInfoByBureau['TransUnion'] = info;
      } else if (bureauName.includes('experian') || bureauName.includes('exp') || bureauName === 'ex') {
        personalInfoByBureau['Experian'] = info;
      } else if (bureauName.includes('equifax') || bureauName.includes('eq')) {
        personalInfoByBureau['Equifax'] = info;
      }
    });

    // Helper to get data from each bureau
    const getBureauData = (field: keyof PersonalInformation) => {
      const getValue = (val: any) => {
        if (!val || val === '' || (Array.isArray(val) && val.length === 0)) return '-';
        return val;
      };
      
      return {
        tu: getValue(personalInfoByBureau['TransUnion']?.[field]),
        ex: getValue(personalInfoByBureau['Experian']?.[field]),
        eq: getValue(personalInfoByBureau['Equifax']?.[field]),
      };
    };

    // Helper to determine status
    const getFieldStatus = (field: keyof PersonalInformation): { label: string; color: string } => {
      const data = getBureauData(field);
      const allValues = [data.tu, data.ex, data.eq];
      const values = allValues.filter(v => v !== '-');
      
      if (values.length === 0) return { label: '', color: '' };
      if (values.length < allValues.length) {
        return { label: 'Partial Match', color: 'text-orange-600 border-orange-200 bg-orange-50' };
      }
      
      const uniqueValues = new Set(values);
      if (uniqueValues.size === 1) {
        return { label: 'Match', color: 'text-green-600 border-green-200 bg-green-50' };
      } else if (uniqueValues.size === values.length) {
        return { label: 'Mismatch', color: 'text-red-600 border-red-200 bg-red-50' };
      } else {
        return { label: 'Partial Match', color: 'text-orange-600 border-orange-200 bg-orange-50' };
      }
    };

    const normalizeAlsoKnownAs = (aliases?: string[]) => {
      if (!aliases || aliases.length === 0) return '-';
      return aliases
        .map(alias => alias.trim())
        .filter(Boolean)
        .join(', ');
    };

    const alsoKnownAsByBureau = {
      tu: normalizeAlsoKnownAs(personalInfoByBureau['TransUnion']?.alsoKnownAs),
      ex: normalizeAlsoKnownAs(personalInfoByBureau['Experian']?.alsoKnownAs),
      eq: normalizeAlsoKnownAs(personalInfoByBureau['Equifax']?.alsoKnownAs),
    };

    const alsoKnownAsStatus = (() => {
      const bureaus: Array<'TransUnion' | 'Experian' | 'Equifax'> = ['TransUnion', 'Experian', 'Equifax'];
      let hasAlsoKnownAs = false;

      for (const bureau of bureaus) {
        const aliases = (personalInfoByBureau[bureau]?.alsoKnownAs || [])
          .map(alias => alias.trim())
          .filter(Boolean);
        const legalName = (personalInfoByBureau[bureau]?.name || '').trim().toLowerCase();

        if (aliases.length === 0) continue;
        hasAlsoKnownAs = true;

        // "Match" only when there is exactly one alias and it equals legal name.
        if (
          aliases.length === 1 &&
          legalName &&
          aliases[0].toLowerCase() === legalName
        ) {
          return { label: 'Match', color: 'text-green-600 border-green-200 bg-green-50' };
        }
      }

      if (!hasAlsoKnownAs) return { label: '', color: '' };
      return { label: 'Mismatch', color: 'text-red-600 border-red-200 bg-red-50' };
    })();

    let maxPrevAddresses = 0;
    Object.values(personalInfoByBureau).forEach(info => {
      if (info.previousAddresses && info.previousAddresses.length > maxPrevAddresses) {
        maxPrevAddresses = info.previousAddresses.length;
      }
    });

    // Get max employment entries across all bureaus
    let maxEmploymentHistory = 0;
    Object.values(personalInfoByBureau).forEach(info => {
      if (info.employmentHistory && info.employmentHistory.length > maxEmploymentHistory) {
        maxEmploymentHistory = info.employmentHistory.length;
      }
    });

    const formatEmploymentHistoryEntry = (entry: any): string => {
      if (!entry) return '-';
      if (typeof entry === 'string') return entry || '-';

      const employer = typeof entry.employer === 'string' ? entry.employer.trim() : '';
      const year = typeof entry.year === 'string' || typeof entry.year === 'number'
        ? String(entry.year).trim()
        : '';

      if (employer && year) return `${employer} (${year})`;
      if (employer) return employer;
      if (year) return year;
      return '-';
    };

    // Calculate summary statistics
    const fields: (keyof PersonalInformation)[] = ['name', 'dob', 'currentAddress'];
    
    // Add previous addresses to fields
    for (let i = 0; i < maxPrevAddresses; i++) {
      fields.push('previousAddresses');
    }

    let matchingFields = 0;
    let mismatches = 0;
    let partialMatches = 0;

    // Count name, dob, currentAddress
    ['name', 'dob', 'currentAddress'].forEach(field => {
      const status = getFieldStatus(field as keyof PersonalInformation);
      if (status.label === 'Match') matchingFields++;
      else if (status.label === 'Mismatch') mismatches++;
      else if (status.label === 'Partial Match') partialMatches++;
    });

    // Count previous addresses
    for (let idx = 0; idx < maxPrevAddresses; idx++) {
      const tuAddr = personalInfoByBureau['TransUnion']?.previousAddresses?.[idx] || '-';
      const exAddr = personalInfoByBureau['Experian']?.previousAddresses?.[idx] || '-';
      const eqAddr = personalInfoByBureau['Equifax']?.previousAddresses?.[idx] || '-';
      
      const allValues = [tuAddr, exAddr, eqAddr];
      const values = allValues.filter(v => v !== '-');
      if (values.length > 0) {
        if (values.length < allValues.length) {
          partialMatches++;
        } else {
          const uniqueValues = new Set(values);
          if (uniqueValues.size === 1) {
            matchingFields++;
          } else if (uniqueValues.size === values.length) {
            mismatches++;
          } else {
            partialMatches++;
          }
        }
      }
    }

    // Count employment history rows
    for (let idx = 0; idx < maxEmploymentHistory; idx++) {
      const tuEmployment = formatEmploymentHistoryEntry(personalInfoByBureau['TransUnion']?.employmentHistory?.[idx]);
      const exEmployment = formatEmploymentHistoryEntry(personalInfoByBureau['Experian']?.employmentHistory?.[idx]);
      const eqEmployment = formatEmploymentHistoryEntry(personalInfoByBureau['Equifax']?.employmentHistory?.[idx]);

      const allValues = [tuEmployment, exEmployment, eqEmployment];
      const values = allValues.filter(v => v !== '-');
      if (values.length > 0) {
        if (values.length < allValues.length) {
          partialMatches++;
        } else {
          const uniqueValues = new Set(values);
          if (uniqueValues.size === 1) {
            matchingFields++;
          } else if (uniqueValues.size === values.length) {
            mismatches++;
          } else {
            partialMatches++;
          }
        }
      }
    }

    return (
      <>
            {personalInfo.length > 0 ? (
              <>
                {/* Summary Cards */}
                <div className="flex gap-4 mb-6 justify-center max-w-7xl mx-auto">
                  {/* Matching Fields Card */}
                  <div className="bg-white border border-gray-200 rounded-xl shadow-sm flex-1 max-w-[480px] h-[108px] p-4 relative">
                    <div className="flex items-center gap-2 mb-4">
                      <CheckCircle2 className="h-5 w-5 text-gray-400" strokeWidth={1.5} />
                      <span className="text-sm font-medium text-gray-900">Matching Fields</span>
                    </div>
                    <div className="text-3xl font-semibold text-gray-900 tracking-tight">
                      {matchingFields}
                    </div>
                  </div>

                  {/* Mismatches Card */}
                  <div className={`bg-white border border-gray-200 ${mismatches > 0 ? 'border-t-2 border-t-red-600' : ''} rounded-xl shadow-sm flex-1 max-w-[480px] h-[108px] p-4 relative`}>
                    <div className="flex items-center gap-2 mb-4">
                      <CircleX className={`h-5 w-5 ${mismatches > 0 ? 'text-red-600' : 'text-gray-400'}`} strokeWidth={1.5} />
                      <span className="text-sm font-medium text-gray-900">Mismatches</span>
                    </div>
                    <div className="text-3xl font-semibold text-gray-900 tracking-tight">
                      {mismatches}
                    </div>
                  </div>

                  {/* Partial Matches Card */}
                  <div className={`bg-white border border-gray-200 ${partialMatches > 0 ? 'border-t-2 border-t-orange-500' : ''} rounded-xl shadow-sm flex-1 max-w-[480px] h-[108px] p-4 relative`}>
                    <div className="flex items-center gap-2 mb-4">
                      <AlertTriangle className={`h-5 w-5 ${partialMatches > 0 ? 'text-orange-500' : 'text-gray-400'}`} strokeWidth={1.5} />
                      <span className="text-sm font-medium text-gray-900">Partial Matches</span>
                    </div>
                    <div className="text-3xl font-semibold text-gray-900 tracking-tight">
                      {partialMatches}
                    </div>
                  </div>
                </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                <Table>
                  <TableHeader className="[&_tr]:border-b-0">
                    {/* Header Row - Title */}
                    <TableRow className="border-b-0">
                      <TableHead colSpan={5} className="px-4 py-3">
                        <div className="flex items-center justify-between">
                          <h2 className="text-xl font-semibold text-gray-900">Personal Information</h2>
                        </div>
                      </TableHead>
                    </TableRow>
                    
                    {/* Column Headers */}
                    <TableRow className="border-b border-b-gray-200 border-t-0">
                      <TableHead className="pl-4 pr-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                        Field
                      </TableHead>
                      <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                        TransUnion
                      </TableHead>
                      <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                        Experian
                      </TableHead>
                      <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                        Equifax
                      </TableHead>
                      <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                        Status
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* Legal Name */}
                    <TableRow className="border-b border-gray-200 border-t border-t-gray-200 hover:bg-gray-50/50 transition-colors">
                      <TableCell className="pl-4 pr-2 py-3 font-medium text-gray-900">Legal Name</TableCell>
                      <TableCell className="px-2 py-3 text-gray-900">{getBureauData('name').tu}</TableCell>
                      <TableCell className="px-2 py-3 text-gray-900">{getBureauData('name').ex}</TableCell>
                      <TableCell className="px-2 py-3 text-gray-900">{getBureauData('name').eq}</TableCell>
                      <TableCell className="px-2 py-3">
                        {getFieldStatus('name').label && (
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded border ${getFieldStatus('name').color}`}>
                            {getFieldStatus('name').label}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>

                    {/* Also Known As */}
                    <TableRow className="border-b border-gray-200 hover:bg-gray-50/50 transition-colors">
                      <TableCell className="pl-4 pr-2 py-3 font-medium text-gray-900">Also Known As</TableCell>
                      <TableCell className="px-2 py-3 text-gray-900">{alsoKnownAsByBureau.tu}</TableCell>
                      <TableCell className="px-2 py-3 text-gray-900">{alsoKnownAsByBureau.ex}</TableCell>
                      <TableCell className="px-2 py-3 text-gray-900">{alsoKnownAsByBureau.eq}</TableCell>
                      <TableCell className="px-2 py-3">
                        {alsoKnownAsStatus.label && (
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded border ${alsoKnownAsStatus.color}`}>
                            {alsoKnownAsStatus.label}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>

                    {/* Date of Birth */}
                    <TableRow className="border-b border-gray-200 hover:bg-gray-50/50 transition-colors">
                      <TableCell className="pl-4 pr-2 py-3 font-medium text-gray-900">Date of Birth</TableCell>
                      <TableCell className="px-2 py-3 text-gray-900">{getBureauData('dob').tu}</TableCell>
                      <TableCell className="px-2 py-3 text-gray-900">{getBureauData('dob').ex}</TableCell>
                      <TableCell className="px-2 py-3 text-gray-900">{getBureauData('dob').eq}</TableCell>
                      <TableCell className="px-2 py-3">
                        {getFieldStatus('dob').label && (
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded border ${getFieldStatus('dob').color}`}>
                            {getFieldStatus('dob').label}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>

                    {/* Current Address */}
                    <TableRow className="border-b border-gray-200 hover:bg-gray-50/50 transition-colors">
                      <TableCell className="pl-4 pr-2 py-3 font-medium text-gray-900">Current Address</TableCell>
                      <TableCell className="px-2 py-3 text-gray-900">{getBureauData('currentAddress').tu}</TableCell>
                      <TableCell className="px-2 py-3 text-gray-900">{getBureauData('currentAddress').ex}</TableCell>
                      <TableCell className="px-2 py-3 text-gray-900">{getBureauData('currentAddress').eq}</TableCell>
                      <TableCell className="px-2 py-3">
                        {getFieldStatus('currentAddress').label && (
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded border ${getFieldStatus('currentAddress').color}`}>
                            {getFieldStatus('currentAddress').label}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>

                    {/* Previous Addresses */}
                    {[...Array(Math.max(maxPrevAddresses, 2))].map((_, idx) => {
                      const tuAddr = personalInfoByBureau['TransUnion']?.previousAddresses?.[idx] || '-';
                      const exAddr = personalInfoByBureau['Experian']?.previousAddresses?.[idx] || '-';
                      const eqAddr = personalInfoByBureau['Equifax']?.previousAddresses?.[idx] || '-';
                      
                      const allValues = [tuAddr, exAddr, eqAddr];
                      const values = allValues.filter(v => v !== '-');
                      
                      let status = { label: '', color: '' };
                      if (values.length > 0) {
                        if (values.length < allValues.length) {
                          status = { label: 'Partial Match', color: 'text-orange-600 border-orange-200 bg-orange-50' };
                        } else {
                          const uniqueValues = new Set(values);
                          if (uniqueValues.size === 1) {
                            status = { label: 'Match', color: 'text-green-600 border-green-200 bg-green-50' };
                          } else if (uniqueValues.size === values.length) {
                            status = { label: 'Mismatch', color: 'text-red-600 border-red-200 bg-red-50' };
                          } else {
                            status = { label: 'Partial Match', color: 'text-orange-600 border-orange-200 bg-orange-50' };
                          }
                        }
                      }

                      return (
                        <TableRow key={`prev-addr-${idx}`} className="border-b border-gray-200 hover:bg-gray-50/50 transition-colors">
                          <TableCell className="pl-4 pr-2 py-3 font-medium text-gray-900">Previous Address {idx + 1}</TableCell>
                          <TableCell className="px-2 py-3 text-gray-900">{tuAddr}</TableCell>
                          <TableCell className="px-2 py-3 text-gray-900">{exAddr}</TableCell>
                          <TableCell className="px-2 py-3 text-gray-900">{eqAddr}</TableCell>
                          <TableCell className="px-2 py-3">
                            {status.label && (
                              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded border ${status.color}`}>
                                {status.label}
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {/* Employment History */}
                    {[...Array(Math.max(maxEmploymentHistory, 1))].map((_, idx) => {
                      const tuEmployment = formatEmploymentHistoryEntry(personalInfoByBureau['TransUnion']?.employmentHistory?.[idx]);
                      const exEmployment = formatEmploymentHistoryEntry(personalInfoByBureau['Experian']?.employmentHistory?.[idx]);
                      const eqEmployment = formatEmploymentHistoryEntry(personalInfoByBureau['Equifax']?.employmentHistory?.[idx]);

                      const allValues = [tuEmployment, exEmployment, eqEmployment];
                      const values = allValues.filter(v => v !== '-');

                      let status = { label: '', color: '' };
                      if (values.length > 0) {
                        if (values.length < allValues.length) {
                          status = { label: 'Partial Match', color: 'text-orange-600 border-orange-200 bg-orange-50' };
                        } else {
                          const uniqueValues = new Set(values);
                          if (uniqueValues.size === 1) {
                            status = { label: 'Match', color: 'text-green-600 border-green-200 bg-green-50' };
                          } else if (uniqueValues.size === values.length) {
                            status = { label: 'Mismatch', color: 'text-red-600 border-red-200 bg-red-50' };
                          } else {
                            status = { label: 'Partial Match', color: 'text-orange-600 border-orange-200 bg-orange-50' };
                          }
                        }
                      }

                      const isLastRow = idx === Math.max(maxEmploymentHistory, 1) - 1;

                      return (
                        <TableRow
                          key={`employment-${idx}`}
                          className={`${isLastRow ? 'border-b-0' : 'border-b'} border-gray-200 hover:bg-gray-50/50 transition-colors`}
                        >
                          <TableCell className="pl-4 pr-2 py-3 font-medium text-gray-900">Employment {idx + 1}</TableCell>
                          <TableCell className="px-2 py-3 text-gray-900">{tuEmployment}</TableCell>
                          <TableCell className="px-2 py-3 text-gray-900">{exEmployment}</TableCell>
                          <TableCell className="px-2 py-3 text-gray-900">{eqEmployment}</TableCell>
                          <TableCell className="px-2 py-3">
                            {status.label && (
                              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded border ${status.color}`}>
                                {status.label}
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              </>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground text-lg">No personal information found.</p>
                <p className="text-sm text-muted-foreground mt-2">Personal information will appear here once your credit report is processed.</p>
              </div>
            )}
      </>
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function renderRawDataTab() {
    return (
      <>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-medium">Complete JSON Response</h4>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyToClipboard}
                    className="flex items-center space-x-2"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-foreground" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                    <span>{copied ? 'Copied!' : 'Copy'}</span>
                  </Button>
                </div>
                <pre className="text-xs bg-muted p-4 rounded-md overflow-auto max-h-[70vh]">
                  {JSON.stringify(analytics, null, 2)}
              </pre>
            </CardContent>
          </Card>
      </>
    );
  }
}
